import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  Plus, Calendar, Users, User, FileText, Clock, 
  ChevronRight, ChevronDown, ChevronUp, Loader2, AlertCircle, CalendarDays,
  Trash2, Eye, X, Lock, UserPlus, ArrowLeft, CheckCircle, Edit, LockKeyhole
} from 'lucide-react';
import ProduccionLayout from '@/components/ProduccionLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Labores Principales
const LABORES_PRINCIPALES = [
  { value: 'cosecha', label: 'Cosecha' },
  { value: 'mantenimiento', label: 'Mantenimiento' },
];

// Labores Específicas por Labor Principal
const LABORES_ESPECIFICAS = {
  cosecha: [
    { value: 'corta', label: 'Corta' },
    { value: 'boyea', label: 'Boyea' },
    { value: 'ajilada', label: 'Ajilada' },
    { value: 'coyolea', label: 'Coyolea' },
    { value: 'alzada_coyol', label: 'Alzada de Coyol' },
  ],
  mantenimiento: [
    { value: 'poda', label: 'Poda' },
    { value: 'rodajea', label: 'Rodajea' },
    { value: 'abonada', label: 'Abonada' },
    { value: 'desmatonada', label: 'Desmatonada' },
    { value: 'chapea_canales', label: 'Chapea de canales' },
    { value: 'chapea_total', label: 'Chapea área total' },
    { value: 'trampeo', label: 'Trampeo' },
  ],
};

// Función para obtener el día de la semana
const getDiaSemana = (fecha) => {
  const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const [dia, mes, año] = fecha.split('-').map(Number);
  const date = new Date(año, mes - 1, dia);
  return dias[date.getDay()];
};

// Función para calcular el periodo de planilla (quincena)
const getPeriodoPlanilla = (fecha) => {
  const [dia, mes, año] = fecha.split('-').map(Number);
  const diaNum = parseInt(dia);
  
  if (diaNum <= 15) {
    return `15-${mes.toString().padStart(2, '0')}-${año}`;
  } else {
    const ultimoDia = new Date(año, mes, 0).getDate();
    return `${ultimoDia}-${mes.toString().padStart(2, '0')}-${año}`;
  }
};

// Función para formatear fecha para mostrar
const formatFechaDisplay = (fecha) => {
  if (!fecha) return '-';
  const [dia, mes, año] = fecha.split('-');
  const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${dia} ${meses[parseInt(mes) - 1]} ${año}`;
};

export default function RegistroLabores({ user, onLogout }) {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estadisticas, setEstadisticas] = useState(null);
  
  // Estados para vista de día
  const [vistaActual, setVistaActual] = useState('lista'); // 'lista' o 'detalle'
  const [registroActivo, setRegistroActivo] = useState(null);
  const [trabajadoresDia, setTrabajadoresDia] = useState([]);
  const [resumenLabores, setResumenLabores] = useState({});
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  
  // Estado para ver detalle de trabajador
  const [verTrabajador, setVerTrabajador] = useState(null);
  const [editarTrabajador, setEditarTrabajador] = useState(null);
  const [laboresEditables, setLaboresEditables] = useState([]);
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [laborEditExpandida, setLaborEditExpandida] = useState(0);
  
  // Estados para agregar nuevos centros en el modal de edición
  const [nuevoCentroEdit, setNuevoCentroEdit] = useState({
    centro_id: '',
    frutas_maduras: '',
    frutas_verdes: '',
    estopas: '',
    hojas_detras: ''
  });
  const [nuevoCentroBoyeaEdit, setNuevoCentroBoyeaEdit] = useState({
    centro_id: '',
    frutas: ''
  });
  const [nuevoCentroCoyoleaEdit, setNuevoCentroCoyoleaEdit] = useState({
    centro_id: '',
    sacos_coyol: ''
  });
  const [nuevoCentroAlzadoEdit, setNuevoCentroAlzadoEdit] = useState({
    centro_id: '',
    sacos: '',
    nota: ''
  });
  
  // Estados para agregar trabajador
  const [showAgregarTrabajador, setShowAgregarTrabajador] = useState(false);
  const [trabajadoresActivos, setTrabajadoresActivos] = useState([]);
  const [loadingTrabajadores, setLoadingTrabajadores] = useState(false);
  const [laborExpandida, setLaborExpandida] = useState(0); // Índice de la labor expandida
  const [formTrabajador, setFormTrabajador] = useState({
    trabajador_id: '',
    trabajador_nombre: '',
    labores: [{ 
      labor_principal: '', 
      labor_especifica: '', 
      cantidad: '', 
      unidad: 'unidades', 
      horas_trabajadas: '', 
      observaciones: '',
      // Campos específicos para labores con centros (Corta, Boyea)
      lote_id: '',
      parcela_id: '',
      // Array de centros (cada uno con sus datos según tipo de labor)
      centros_cortados: [],  // Para Corta
      centros_boyeados: []   // Para Boyea
    }]
  });
  
  // Estado temporal para agregar un nuevo centro (Corta)
  const [nuevoCentro, setNuevoCentro] = useState({
    centro_id: '',
    frutas_maduras: '',
    frutas_verdes: '',
    estopas: '',
    hojas_detras: '',
    no_terminado: false
  });
  
  // Estado temporal para agregar un nuevo centro (Boyea)
  const [nuevoCentroBoyea, setNuevoCentroBoyea] = useState({
    centro_id: '',
    frutas: '',
    no_terminado: false
  });
  
  // Estado temporal para agregar un nuevo centro (Coyolea)
  const [nuevoCentroCoyolea, setNuevoCentroCoyolea] = useState({
    centro_id: '',
    sacos_coyol: '',
    no_terminado: false
  });
  
  // Estado temporal para agregar un nuevo centro (Alzada de Coyol)
  const [nuevoCentroAlzado, setNuevoCentroAlzado] = useState({
    centro_id: '',
    sacos: '',
    nota: '',
    no_terminado: false
  });
  
  // Estados para lotes, parcelas y centros (para labor Corta)
  const [lotes, setLotes] = useState([]);
  const [parcelasPorLote, setParcelasPorLote] = useState({});
  const [centrosPorParcela, setCentrosPorParcela] = useState({});
  const [loadingLotes, setLoadingLotes] = useState(false);
  const [guardandoTrabajador, setGuardandoTrabajador] = useState(false);
  
  // Estados para confirmaciones
  const [showConfirmCerrar, setShowConfirmCerrar] = useState(false);
  const [cerrandoDia, setCerrandoDia] = useState(false);
  
  // Estados para crear nuevo día
  const [showCrearDia, setShowCrearDia] = useState(false);
  const [creandoDia, setCreandoDia] = useState(false);
  const [formNuevoDia, setFormNuevoDia] = useState({
    fecha: '',
    periodo_planilla: ''
  });
  
  // Estados para calendario de planillas
  const [calendarioPlanillas, setCalendarioPlanillas] = useState([]);
  const [loadingCalendario, setLoadingCalendario] = useState(false);

  const isSuperAdmin = user?.is_admin || user?.role === 'admin';

  useEffect(() => {
    fetchRegistros();
    fetchEstadisticas();
    fetchCalendarioPlanillas();
    fetchLotes();
  }, []);

  // Función para cargar lotes
  const fetchLotes = async () => {
    try {
      setLoadingLotes(true);
      const response = await axios.get(`${API}/lotes`);
      setLotes(response.data || []);
    } catch (error) {
      console.error('Error cargando lotes:', error);
    } finally {
      setLoadingLotes(false);
    }
  };

  // Función para cargar parcelas de un lote específico (extraídas de centros)
  const fetchParcelasPorLote = async (loteId) => {
    if (parcelasPorLote[loteId]) return; // Ya están cargadas
    try {
      // Obtener centros del lote para extraer parcelas únicas
      const response = await axios.get(`${API}/centros?lote_id=${loteId}`);
      const centros = response.data || [];
      
      // Extraer parcelas únicas de los centros
      const parcelasUnicas = [...new Set(centros.map(c => c.parcela_numero))].sort((a, b) => a - b);
      const parcelas = parcelasUnicas.map(num => ({
        id: `${loteId}_parcela_${num}`, // ID virtual para la parcela
        numero: num,
        lote_id: loteId
      }));
      
      setParcelasPorLote(prev => ({
        ...prev,
        [loteId]: parcelas
      }));
      
      // También guardar los centros por lote para filtrar después por parcela
      setCentrosPorParcela(prev => ({
        ...prev,
        [`${loteId}_all`]: centros
      }));
    } catch (error) {
      console.error('Error cargando parcelas:', error);
    }
  };

  // Función para obtener centros filtrados por lote y parcela_numero
  const getCentrosPorParcela = (loteId, parcelaNumero) => {
    const todosCentros = centrosPorParcela[`${loteId}_all`] || [];
    return todosCentros.filter(c => c.parcela_numero === parcelaNumero);
  };

  const fetchRegistros = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/registros-labores?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistros(response.data.registros || []);
    } catch (error) {
      console.error('Error cargando registros:', error);
      toast.error('Error al cargar los registros');
    } finally {
      setLoading(false);
    }
  };

  const fetchEstadisticas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/registros-labores/resumen/estadisticas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEstadisticas(response.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const fetchCalendarioPlanillas = async () => {
    try {
      setLoadingCalendario(true);
      const response = await axios.get(`${API}/planilla/calendario`);
      setCalendarioPlanillas(response.data || []);
    } catch (error) {
      console.error('Error cargando calendario de planillas:', error);
      // Si no hay calendario configurado, dejamos el array vacío
      setCalendarioPlanillas([]);
    } finally {
      setLoadingCalendario(false);
    }
  };

  const fetchDetalleDia = async (registroId) => {
    try {
      setLoadingDetalle(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/registros-labores/${registroId}/trabajadores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistroActivo(response.data.registro);
      setTrabajadoresDia(response.data.trabajadores || []);
      setResumenLabores(response.data.resumen_por_labor || {});
    } catch (error) {
      console.error('Error cargando detalle:', error);
      toast.error('Error al cargar el detalle del día');
    } finally {
      setLoadingDetalle(false);
    }
  };

  const fetchTrabajadoresActivos = async () => {
    try {
      setLoadingTrabajadores(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/trabajadores/activos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrabajadoresActivos(response.data || []);
    } catch (error) {
      console.error('Error cargando trabajadores:', error);
      toast.error('Error al cargar trabajadores');
    } finally {
      setLoadingTrabajadores(false);
    }
  };

  const handleRegistrarDia = () => {
    // Abrir dialog para crear día con fecha y periodo seleccionables
    const hoy = new Date();
    const fechaHoy = `${hoy.getFullYear()}-${(hoy.getMonth() + 1).toString().padStart(2, '0')}-${hoy.getDate().toString().padStart(2, '0')}`;
    
    setFormNuevoDia({
      fecha: fechaHoy,
      periodo_planilla: ''
    });
    setShowCrearDia(true);
  };

  const confirmarCrearDia = async () => {
    if (!formNuevoDia.fecha || !formNuevoDia.periodo_planilla) {
      toast.error('Selecciona la fecha y el periodo de planilla');
      return;
    }

    setCreandoDia(true);
    try {
      const token = localStorage.getItem('token');
      
      // Convertir fecha de YYYY-MM-DD a DD-MM-YYYY
      const [año, mes, dia] = formNuevoDia.fecha.split('-');
      const fechaFormateada = `${dia}-${mes}-${año}`;
      
      // Convertir periodo de YYYY-MM-DD a DD-MM-YYYY
      const [añoP, mesP, diaP] = formNuevoDia.periodo_planilla.split('-');
      const periodoFormateado = `${diaP}-${mesP}-${añoP}`;
      
      const nuevoRegistro = {
        fecha: fechaFormateada,
        dia_semana: getDiaSemana(fechaFormateada),
        periodo_planilla: periodoFormateado,
        trabajadores_registrados: 0,
        registrado_por_id: user?.id || 'unknown',
        registrado_por_nombre: user?.nombre || user?.username || 'Admin',
        estado: 'activo'
      };

      const response = await axios.post(`${API}/registros-labores`, nuevoRegistro, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Día registrado exitosamente');
      setShowCrearDia(false);
      
      // Ir directamente al detalle del día creado
      await fetchDetalleDia(response.data.registro.id);
      setVistaActual('detalle');
      fetchRegistros();
      fetchEstadisticas();
      
    } catch (error) {
      console.error('Error al registrar día:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al registrar el día');
      }
    } finally {
      setCreandoDia(false);
    }
  };

  // Generar opciones de periodos de planilla desde el calendario configurado
  const getOpcionesPeriodos = () => {
    // Si hay calendario de planillas configurado, usarlo
    if (calendarioPlanillas.length > 0) {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      
      // Filtrar solo planillas futuras o actuales (próximas 6)
      const opcionesFuturas = calendarioPlanillas
        .filter(p => {
          const fechaPago = new Date(p.fecha_pago + 'T00:00:00');
          // Mostrar planillas de los últimos 15 días hacia el futuro
          const fechaLimiteAnterior = new Date(hoy);
          fechaLimiteAnterior.setDate(fechaLimiteAnterior.getDate() - 15);
          return fechaPago >= fechaLimiteAnterior;
        })
        .slice(0, 6)
        .map(p => {
          const [año, mes, dia] = p.fecha_pago.split('-');
          const fechaFormateada = `${año}-${mes}-${dia}`;
          const fecha = new Date(p.fecha_pago + 'T00:00:00');
          const label = `${dia} de ${getNombreMes(parseInt(mes))} ${año} (Planilla #${p.numero})`;
          return { value: fechaFormateada, label };
        });
      
      if (opcionesFuturas.length > 0) {
        return opcionesFuturas;
      }
    }
    
    // Fallback: generar opciones manualmente si no hay calendario
    const periodos = [];
    const hoy = new Date();
    
    for (let i = 0; i < 4; i++) {
      const fecha = new Date(hoy);
      fecha.setMonth(fecha.getMonth() + Math.floor(i / 2));
      
      const mes = fecha.getMonth() + 1;
      const año = fecha.getFullYear();
      const ultimoDia = new Date(año, mes, 0).getDate();
      
      // Primera quincena (día 15)
      if (i % 2 === 0 || i === 0) {
        const fecha15 = `${año}-${mes.toString().padStart(2, '0')}-15`;
        const label15 = `15 de ${getNombreMes(mes)} ${año}`;
        if (!periodos.find(p => p.value === fecha15)) {
          periodos.push({ value: fecha15, label: label15 });
        }
      }
      
      // Segunda quincena (último día del mes)
      const fechaUltimo = `${año}-${mes.toString().padStart(2, '0')}-${ultimoDia}`;
      const labelUltimo = `${ultimoDia} de ${getNombreMes(mes)} ${año}`;
      if (!periodos.find(p => p.value === fechaUltimo)) {
        periodos.push({ value: fechaUltimo, label: labelUltimo });
      }
    }
    
    return periodos;
  };

  const getNombreMes = (mes) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return meses[mes - 1];
  };

  // Auto-seleccionar periodo de planilla basado en la fecha seleccionada
  const autoSeleccionarPeriodo = (fechaTrabajo) => {
    if (!fechaTrabajo || calendarioPlanillas.length === 0) return null;
    
    // Encontrar la planilla cuyo período incluya la fecha de trabajo
    const fechaTrabajoDate = new Date(fechaTrabajo + 'T00:00:00');
    
    const planillaCorrespondiente = calendarioPlanillas.find(p => {
      const inicioP = new Date(p.periodo_inicio + 'T00:00:00');
      const finP = new Date(p.periodo_fin + 'T00:00:00');
      return fechaTrabajoDate >= inicioP && fechaTrabajoDate <= finP;
    });
    
    // Si no se encuentra una planilla que contenga la fecha, buscar la próxima
    if (!planillaCorrespondiente) {
      const proximaPlanilla = calendarioPlanillas.find(p => {
        const fechaPago = new Date(p.fecha_pago + 'T00:00:00');
        return fechaPago > fechaTrabajoDate;
      });
      return proximaPlanilla?.fecha_pago || null;
    }
    
    return planillaCorrespondiente.fecha_pago;
  };

  // Handler para cuando cambia la fecha de trabajo
  const handleFechaChange = (nuevaFecha) => {
    setFormNuevoDia(prev => {
      const periodoAuto = autoSeleccionarPeriodo(nuevaFecha);
      return {
        ...prev,
        fecha: nuevaFecha,
        periodo_planilla: periodoAuto || prev.periodo_planilla
      };
    });
  };

  const handleVerDia = async (registro) => {
    await fetchDetalleDia(registro.id);
    setVistaActual('detalle');
  };

  const handleVolverLista = () => {
    setVistaActual('lista');
    setRegistroActivo(null);
    setTrabajadoresDia([]);
    setResumenLabores({});
    fetchRegistros();
  };

  const handleAbrirAgregarTrabajador = () => {
    fetchTrabajadoresActivos();
    setFormTrabajador({
      trabajador_id: '',
      trabajador_nombre: '',
      labores: [{ labor_principal: '', labor_especifica: '', cantidad: '', unidad: 'unidades', horas_trabajadas: '', observaciones: '' }]
    });
    setShowAgregarTrabajador(true);
  };

  const handleSeleccionarTrabajador = (trabajadorId) => {
    const trabajador = trabajadoresActivos.find(t => t.id === trabajadorId);
    if (trabajador) {
      setFormTrabajador(prev => ({
        ...prev,
        trabajador_id: trabajador.id,
        trabajador_nombre: trabajador.nombre_completo
      }));
    }
  };

  // Agregar otra labor al formulario
  const handleAgregarOtraLabor = () => {
    const newIndex = formTrabajador.labores.length;
    setFormTrabajador(prev => ({
      ...prev,
      labores: [...prev.labores, { 
        labor_principal: '', 
        labor_especifica: '', 
        cantidad: '', 
        unidad: 'unidades', 
        horas_trabajadas: '', 
        observaciones: '',
        // Campos específicos para labores con centros
        lote_id: '',
        parcela_id: '',
        centros_cortados: [],
        centros_boyeados: [],
        centros_coyoleados: [],
        centros_alzados: [],  // Para Alzada de Coyol
        // Campos específicos para Ajilada (pago por horas)
        hora_inicio: '',
        hora_salida: '',
        horas_ordinarias: 0,
        horas_extra: 0,
        minutos_extra: 0,
        cortero_vinculado_id: '',  // ID del cortero al que se le ajiló
        cortero_vinculado_nombre: '',
        frutas_ajiladas: 0  // Se calcula desde el cortero
      }]
    }));
    setLaborExpandida(newIndex); // Expandir la nueva labor automáticamente
  };
  
  // Agregar un centro cortado a una labor específica (Corta)
  const handleAgregarCentroCortado = (laborIndex) => {
    const labor = formTrabajador.labores[laborIndex];
    if (!nuevoCentro.centro_id) {
      toast.error('Selecciona un centro frutero');
      return;
    }
    
    // Verificar que no esté duplicado (mismo centro_id)
    if (labor.centros_cortados.some(c => c.centro_id === nuevoCentro.centro_id)) {
      toast.error('Este centro ya fue agregado');
      return;
    }
    
    // Obtener info del centro y lote para mostrar
    const parcelaNumero = parseInt(labor.parcela_id.split('_parcela_')[1]);
    const centros = getCentrosPorParcela(labor.lote_id, parcelaNumero);
    const centroInfo = centros.find(c => c.id === nuevoCentro.centro_id);
    const loteInfo = lotes.find(l => l.id === labor.lote_id);
    
    // Guardar el centro CON su lote y parcela (independiente del selector)
    const nuevoCentroCompleto = {
      ...nuevoCentro,
      centro_numero: centroInfo?.numero_centro || centroInfo?.numero || '?',
      lote_id: labor.lote_id,
      lote_nombre: loteInfo?.nombre || '',
      parcela_numero: parcelaNumero
    };
    
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((l, i) => {
        if (i !== laborIndex) return l;
        return {
          ...l,
          centros_cortados: [...l.centros_cortados, nuevoCentroCompleto]
        };
      })
    }));
    
    // Limpiar el formulario de nuevo centro
    setNuevoCentro({
      centro_id: '',
      frutas_maduras: '',
      frutas_verdes: '',
      estopas: '',
      hojas_detras: '',
      no_terminado: false
    });
    
    toast.success(`Centro ${nuevoCentroCompleto.centro_numero} (${loteInfo?.nombre || ''}) agregado`);
  };
  
  // Agregar un centro boyeado a una labor específica (Boyea)
  const handleAgregarCentroBoyeado = (laborIndex) => {
    const labor = formTrabajador.labores[laborIndex];
    if (!nuevoCentroBoyea.centro_id) {
      toast.error('Selecciona un centro frutero');
      return;
    }
    
    // Verificar que no esté duplicado (mismo centro_id)
    if (labor.centros_boyeados.some(c => c.centro_id === nuevoCentroBoyea.centro_id)) {
      toast.error('Este centro ya fue agregado');
      return;
    }
    
    // Obtener info del centro y lote para mostrar
    const parcelaNumero = parseInt(labor.parcela_id.split('_parcela_')[1]);
    const centros = getCentrosPorParcela(labor.lote_id, parcelaNumero);
    const centroInfo = centros.find(c => c.id === nuevoCentroBoyea.centro_id);
    const loteInfo = lotes.find(l => l.id === labor.lote_id);
    
    // Guardar el centro CON su lote y parcela (independiente del selector)
    const nuevoCentroCompleto = {
      ...nuevoCentroBoyea,
      centro_numero: centroInfo?.numero_centro || centroInfo?.numero || '?',
      lote_id: labor.lote_id,
      lote_nombre: loteInfo?.nombre || '',
      parcela_numero: parcelaNumero
    };
    
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((l, i) => {
        if (i !== laborIndex) return l;
        return {
          ...l,
          centros_boyeados: [...l.centros_boyeados, nuevoCentroCompleto]
        };
      })
    }));
    
    // Limpiar el formulario de nuevo centro
    setNuevoCentroBoyea({
      centro_id: '',
      frutas: '',
      no_terminado: false
    });
    
    toast.success(`Centro ${nuevoCentroCompleto.centro_numero} (${loteInfo?.nombre || ''}) agregado`);
  };
  
  // Eliminar un centro cortado
  const handleEliminarCentroCortado = (laborIndex, centroIndex) => {
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((l, i) => {
        if (i !== laborIndex) return l;
        return {
          ...l,
          centros_cortados: l.centros_cortados.filter((_, ci) => ci !== centroIndex)
        };
      })
    }));
  };
  
  // Eliminar un centro boyeado
  const handleEliminarCentroBoyeado = (laborIndex, centroIndex) => {
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((l, i) => {
        if (i !== laborIndex) return l;
        return {
          ...l,
          centros_boyeados: l.centros_boyeados.filter((_, ci) => ci !== centroIndex)
        };
      })
    }));
  };

  // Actualizar campo no_terminado de un centro cortado
  const handleToggleNoTerminadoCorta = (laborIndex, centroIndex) => {
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((l, i) => {
        if (i !== laborIndex) return l;
        return {
          ...l,
          centros_cortados: l.centros_cortados.map((c, ci) => {
            if (ci !== centroIndex) return c;
            return { ...c, no_terminado: !c.no_terminado };
          })
        };
      })
    }));
  };

  // Actualizar campo no_terminado de un centro boyeado
  const handleToggleNoTerminadoBoyea = (laborIndex, centroIndex) => {
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((l, i) => {
        if (i !== laborIndex) return l;
        return {
          ...l,
          centros_boyeados: l.centros_boyeados.map((c, ci) => {
            if (ci !== centroIndex) return c;
            return { ...c, no_terminado: !c.no_terminado };
          })
        };
      })
    }));
  };

  // Agregar un centro coyoleado a una labor específica (Coyolea)
  const handleAgregarCentroCoyoleado = (laborIndex) => {
    const labor = formTrabajador.labores[laborIndex];
    if (!nuevoCentroCoyolea.centro_id) {
      toast.error('Selecciona un centro frutero');
      return;
    }
    
    // Verificar que no esté duplicado (mismo centro_id)
    if ((labor.centros_coyoleados || []).some(c => c.centro_id === nuevoCentroCoyolea.centro_id)) {
      toast.error('Este centro ya fue agregado');
      return;
    }
    
    // Obtener info del centro y lote para mostrar
    const parcelaNumero = parseInt(labor.parcela_id.split('_parcela_')[1]);
    const centros = getCentrosPorParcela(labor.lote_id, parcelaNumero);
    const centroInfo = centros.find(c => c.id === nuevoCentroCoyolea.centro_id);
    const loteInfo = lotes.find(l => l.id === labor.lote_id);
    
    // Guardar el centro CON su lote y parcela (independiente del selector)
    const nuevoCentroCompleto = {
      ...nuevoCentroCoyolea,
      centro_numero: centroInfo?.numero_centro || centroInfo?.numero || '?',
      lote_id: labor.lote_id,
      lote_nombre: loteInfo?.nombre || '',
      parcela_numero: parcelaNumero
    };
    
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((l, i) => {
        if (i !== laborIndex) return l;
        return {
          ...l,
          centros_coyoleados: [...l.centros_coyoleados, nuevoCentroCompleto]
        };
      })
    }));
    
    // Limpiar el formulario de nuevo centro
    setNuevoCentroCoyolea({
      centro_id: '',
      sacos_coyol: '',
      no_terminado: false
    });
    
    toast.success(`Centro ${nuevoCentroCompleto.centro_numero} (${loteInfo?.nombre || ''}) agregado`);
  };
  
  // Eliminar un centro coyoleado
  const handleEliminarCentroCoyoleado = (laborIndex, centroIndex) => {
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((l, i) => {
        if (i !== laborIndex) return l;
        return {
          ...l,
          centros_coyoleados: l.centros_coyoleados.filter((_, ci) => ci !== centroIndex)
        };
      })
    }));
  };

  // Actualizar campo no_terminado de un centro coyoleado
  const handleToggleNoTerminadoCoyolea = (laborIndex, centroIndex) => {
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((l, i) => {
        if (i !== laborIndex) return l;
        return {
          ...l,
          centros_coyoleados: l.centros_coyoleados.map((c, ci) => {
            if (ci !== centroIndex) return c;
            return { ...c, no_terminado: !c.no_terminado };
          })
        };
      })
    }));
  };

  // Agregar un centro alzado a una labor específica (Alzada de Coyol)
  const handleAgregarCentroAlzado = (laborIndex) => {
    const labor = formTrabajador.labores[laborIndex];
    if (!nuevoCentroAlzado.centro_id) {
      toast.error('Selecciona un centro frutero');
      return;
    }
    if (!nuevoCentroAlzado.sacos || parseFloat(nuevoCentroAlzado.sacos) < 0.5) {
      toast.error('Ingresa al menos 0.5 sacos');
      return;
    }
    
    // Verificar que no esté duplicado
    if ((labor.centros_alzados || []).some(c => c.centro_id === nuevoCentroAlzado.centro_id)) {
      toast.error('Este centro ya fue agregado');
      return;
    }
    
    // Obtener info del centro y lote
    const parcelaNumero = parseInt(labor.parcela_id.split('_parcela_')[1]);
    const centros = getCentrosPorParcela(labor.lote_id, parcelaNumero);
    const centroInfo = centros.find(c => c.id === nuevoCentroAlzado.centro_id);
    const loteInfo = lotes.find(l => l.id === labor.lote_id);
    
    const nuevoCentroCompleto = {
      ...nuevoCentroAlzado,
      centro_numero: centroInfo?.numero_centro || centroInfo?.numero || '?',
      lote_id: labor.lote_id,
      lote_nombre: loteInfo?.nombre || '',
      parcela_numero: parcelaNumero,
      sacos: parseFloat(nuevoCentroAlzado.sacos)
    };
    
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((l, i) => {
        if (i !== laborIndex) return l;
        return {
          ...l,
          centros_alzados: [...(l.centros_alzados || []), nuevoCentroCompleto]
        };
      })
    }));
    
    // Limpiar formulario
    setNuevoCentroAlzado({
      centro_id: '',
      sacos: '',
      nota: '',
      no_terminado: false
    });
    
    toast.success(`Centro ${nuevoCentroCompleto.centro_numero} (${loteInfo?.nombre || ''}) agregado`);
  };
  
  // Eliminar un centro alzado
  const handleEliminarCentroAlzado = (laborIndex, centroIndex) => {
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((l, i) => {
        if (i !== laborIndex) return l;
        return {
          ...l,
          centros_alzados: (l.centros_alzados || []).filter((_, ci) => ci !== centroIndex)
        };
      })
    }));
  };

  // Actualizar campo no_terminado de un centro alzado
  const handleToggleNoTerminadoAlzado = (laborIndex, centroIndex) => {
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((l, i) => {
        if (i !== laborIndex) return l;
        return {
          ...l,
          centros_alzados: (l.centros_alzados || []).map((c, ci) => {
            if (ci !== centroIndex) return c;
            return { ...c, no_terminado: !c.no_terminado };
          })
        };
      })
    }));
  };

  // Eliminar una labor del formulario
  const handleEliminarLaborForm = (index) => {
    if (formTrabajador.labores.length === 1) return; // Mantener al menos una
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.filter((_, i) => i !== index)
    }));
  };

  // Calcular horas ordinarias y extra para Ajilada
  const calcularHorasAjilada = (horaInicio, horaSalida) => {
    if (!horaInicio || !horaSalida) {
      return { horas_ordinarias: 0, horas_extra: 0, minutos_extra: 0, total_display: '0h 0min' };
    }
    
    const [hInicio, mInicio] = horaInicio.split(':').map(Number);
    const [hSalida, mSalida] = horaSalida.split(':').map(Number);
    
    let minutosInicio = hInicio * 60 + mInicio;
    let minutosSalida = hSalida * 60 + mSalida;
    
    // Si salida es menor que inicio, asumimos que pasó la medianoche
    if (minutosSalida < minutosInicio) {
      minutosSalida += 24 * 60;
    }
    
    const totalMinutos = minutosSalida - minutosInicio;
    const totalHoras = totalMinutos / 60;
    
    // 8 horas = 480 minutos ordinarios
    const minutosOrdinarios = Math.min(totalMinutos, 480);
    const minutosExtra = Math.max(0, totalMinutos - 480);
    
    const horasOrdinarias = Math.floor(minutosOrdinarios / 60);
    const minutosOrdinariosResto = minutosOrdinarios % 60;
    
    const horasExtra = Math.floor(minutosExtra / 60);
    const minutosExtraResto = minutosExtra % 60;
    
    // Formato de display
    let totalDisplay = '';
    if (horasOrdinarias > 0 || minutosOrdinariosResto > 0) {
      totalDisplay = `${horasOrdinarias}h ${minutosOrdinariosResto}min`;
    }
    if (horasExtra > 0 || minutosExtraResto > 0) {
      totalDisplay += ` + ${horasExtra}h ${minutosExtraResto}min extra`;
    }
    
    return {
      horas_ordinarias: minutosOrdinarios / 60,
      horas_extra: horasExtra,
      minutos_extra: minutosExtraResto,
      total_display: totalDisplay || '0h 0min'
    };
  };

  // Actualizar una labor específica
  const handleUpdateLabor = (index, field, value) => {
    setFormTrabajador(prev => ({
      ...prev,
      labores: prev.labores.map((labor, i) => {
        if (i !== index) return labor;
        
        // Si cambia labor_principal, resetear labor_especifica y campos
        if (field === 'labor_principal') {
          return { 
            ...labor, 
            labor_principal: value, 
            labor_especifica: '',
            lote_id: '',
            parcela_id: '',
            centros_cortados: [],
            centros_boyeados: []
          };
        }
        
        // Si cambia labor_especifica, resetear campos según tipo
        if (field === 'labor_especifica') {
          const needsCentros = value === 'corta' || value === 'boyea';
          return { 
            ...labor, 
            labor_especifica: value,
            lote_id: needsCentros ? labor.lote_id : '',
            parcela_id: needsCentros ? labor.parcela_id : '',
            centros_cortados: [],
            centros_boyeados: []
          };
        }
        
        // Si cambia lote_id, NO resetear centros (solo resetear parcela para navegación)
        if (field === 'lote_id') {
          // Cargar parcelas del lote seleccionado
          if (value) {
            fetchParcelasPorLote(value);
          }
          return { 
            ...labor, 
            lote_id: value,
            parcela_id: ''
            // NO resetear centros_cortados ni centros_boyeados
          };
        }
        
        // Si cambia parcela_id, NO resetear centros
        if (field === 'parcela_id') {
          return { 
            ...labor, 
            parcela_id: value
            // NO resetear centros_cortados ni centros_boyeados
          };
        }
        
        // Si cambia hora_inicio o hora_salida para Ajilada, recalcular
        if ((field === 'hora_inicio' || field === 'hora_salida') && labor.labor_especifica === 'ajilada') {
          const newLabor = { ...labor, [field]: value };
          const calculo = calcularHorasAjilada(newLabor.hora_inicio, newLabor.hora_salida);
          return {
            ...newLabor,
            horas_ordinarias: calculo.horas_ordinarias,
            horas_extra: calculo.horas_extra,
            minutos_extra: calculo.minutos_extra
          };
        }
        
        return { ...labor, [field]: value };
      })
    }));
    
    // Limpiar los formularios de nuevo centro cuando cambia lote o parcela
    if (field === 'lote_id' || field === 'parcela_id') {
      setNuevoCentro({
        centro_id: '',
        frutas_maduras: '',
        frutas_verdes: '',
        estopas: '',
        hojas_detras: ''
      });
      setNuevoCentroBoyea({
        centro_id: '',
        frutas: ''
      });
    }
  };

  const handleGuardarTrabajador = async () => {
    if (!formTrabajador.trabajador_id) {
      toast.error('Selecciona un trabajador');
      return;
    }
    
    // Verificar que al menos una labor tenga labor principal y específica
    const laboresValidas = formTrabajador.labores.filter(l => l.labor_principal && l.labor_especifica);
    if (laboresValidas.length === 0) {
      toast.error('Selecciona labor principal y específica');
      return;
    }
    
    // Validar campos de Corta y Boyea si es necesario
    for (const labor of laboresValidas) {
      if (labor.labor_especifica === 'corta') {
        if (labor.centros_cortados.length === 0) {
          toast.error('Para la labor "Corta", debes agregar al menos un centro');
          return;
        }
      }
      if (labor.labor_especifica === 'boyea') {
        if (labor.centros_boyeados.length === 0) {
          toast.error('Para la labor "Boyea", debes agregar al menos un centro');
          return;
        }
      }
      if (labor.labor_especifica === 'ajilada') {
        if (!labor.hora_inicio || !labor.hora_salida) {
          toast.error('Para la labor "Ajilada", debes ingresar hora de inicio y salida');
          return;
        }
      }
      if (labor.labor_especifica === 'coyolea') {
        if ((labor.centros_coyoleados || []).length === 0) {
          toast.error('Para la labor "Coyolea", debes agregar al menos un centro');
          return;
        }
      }
      if (labor.labor_especifica === 'alzada_coyol') {
        if ((labor.centros_alzados || []).length === 0) {
          toast.error('Para la labor "Alzada de Coyol", debes agregar al menos un centro');
          return;
        }
      }
    }

    setGuardandoTrabajador(true);
    try {
      const token = localStorage.getItem('token');
      const laboresValidas = formTrabajador.labores.filter(l => l.labor_principal && l.labor_especifica);
      
      // Guardar cada labor por separado
      for (const labor of laboresValidas) {
        const payload = {
          registro_dia_id: registroActivo.id,
          trabajador_id: formTrabajador.trabajador_id,
          trabajador_nombre: formTrabajador.trabajador_nombre,
          labor_principal: labor.labor_principal,
          labor_especifica: labor.labor_especifica,
          cantidad: parseFloat(labor.cantidad) || 0,
          unidad: labor.unidad,
          horas_trabajadas: parseFloat(labor.horas_trabajadas) || 0,
          observaciones: labor.observaciones || null
        };
        
        // Agregar campos específicos de Corta si aplica
        // Cada centro tiene su propio lote/parcela
        if (labor.labor_especifica === 'corta') {
          payload.datos_corta = {
            centros_cortados: labor.centros_cortados.map(c => ({
              centro_id: c.centro_id,
              lote_id: c.lote_id,
              parcela_numero: c.parcela_numero,
              frutas_maduras: parseInt(c.frutas_maduras) || 0,
              frutas_verdes: parseInt(c.frutas_verdes) || 0,
              estopas: parseInt(c.estopas) || 0,
              hojas_detras: parseInt(c.hojas_detras) || 0,
              no_terminado: c.no_terminado || false
            }))
          };
        }
        
        // Agregar campos específicos de Boyea si aplica
        // Cada centro tiene su propio lote/parcela
        if (labor.labor_especifica === 'boyea') {
          payload.datos_boyea = {
            centros_boyeados: labor.centros_boyeados.map(c => ({
              centro_id: c.centro_id,
              lote_id: c.lote_id,
              parcela_numero: c.parcela_numero,
              frutas: parseInt(c.frutas) || 0,
              no_terminado: c.no_terminado || false
            }))
          };
        }
        
        // Agregar campos específicos de Ajilada si aplica
        if (labor.labor_especifica === 'ajilada') {
          payload.datos_ajilada = {
            hora_inicio: labor.hora_inicio,
            hora_salida: labor.hora_salida,
            horas_ordinarias: labor.horas_ordinarias || 0,
            horas_extra: labor.horas_extra || 0,
            minutos_extra: labor.minutos_extra || 0,
            cortero_vinculado_id: labor.cortero_vinculado_id || null,
            cortero_vinculado_nombre: labor.cortero_vinculado_nombre || null,
            frutas_ajiladas: labor.frutas_ajiladas || 0
          };
        }
        
        // Agregar campos específicos de Coyolea si aplica
        if (labor.labor_especifica === 'coyolea') {
          payload.datos_coyolea = {
            centros_coyoleados: labor.centros_coyoleados.map(c => ({
              centro_id: c.centro_id,
              lote_id: c.lote_id,
              parcela_numero: c.parcela_numero,
              sacos_coyol: parseInt(c.sacos_coyol) || 0,
              no_terminado: c.no_terminado || false
            }))
          };
        }
        
        // Agregar campos específicos de Alzada de Coyol si aplica
        if (labor.labor_especifica === 'alzada_coyol') {
          payload.datos_alzada_coyol = {
            centros_alzados: (labor.centros_alzados || []).map(c => ({
              centro_id: c.centro_id,
              lote_id: c.lote_id,
              parcela_numero: c.parcela_numero,
              sacos: parseFloat(c.sacos) || 0,
              nota: c.nota || null,
              no_terminado: c.no_terminado || false
            }))
          };
        }
        
        await axios.post(`${API}/registros-labores/${registroActivo.id}/trabajadores`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success(`${laboresValidas.length} ${laboresValidas.length === 1 ? 'labor agregada' : 'labores agregadas'} exitosamente`);
      setShowAgregarTrabajador(false);
      await fetchDetalleDia(registroActivo.id);
      
    } catch (error) {
      console.error('Error agregando trabajador:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al agregar trabajador');
      }
    } finally {
      setGuardandoTrabajador(false);
    }
  };

  const handleEliminarTrabajador = async (laborId) => {
    if (!confirm('¿Eliminar esta labor?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/registros-labores/${registroActivo.id}/trabajadores/${laborId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Labor eliminada');
      await fetchDetalleDia(registroActivo.id);
    } catch (error) {
      console.error('Error eliminando labor:', error);
      toast.error('Error al eliminar labor');
    }
  };
  
  // Eliminar todas las labores de un trabajador
  const handleEliminarTrabajadorCompleto = async (trabajadorId, trabajadorNombre, labores) => {
    if (!confirm(`¿Eliminar TODAS las labores de ${trabajadorNombre}? (${labores.length} ${labores.length === 1 ? 'labor' : 'labores'})`)) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Eliminar cada labor del trabajador
      for (const labor of labores) {
        await axios.delete(`${API}/registros-labores/${registroActivo.id}/trabajadores/${labor.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success(`Se eliminaron todas las labores de ${trabajadorNombre}`);
      await fetchDetalleDia(registroActivo.id);
    } catch (error) {
      console.error('Error eliminando trabajador:', error);
      toast.error('Error al eliminar trabajador');
    }
  };

  // Abrir modal de edición y preparar datos editables
  const handleAbrirEdicion = (trabajadorId, trabajadorNombre, labores) => {
    // Convertir las labores a un formato editable
    const laboresParaEditar = labores.map(labor => {
      // Preparar centros para Corta
      let centros_cortados = [];
      if (labor.labor_especifica === 'corta' && labor.datos_corta?.centros_cortados) {
        centros_cortados = labor.datos_corta.centros_cortados.map(c => ({
          centro_id: c.centro_id,
          centro_numero: c.centro_numero,
          lote_id: c.lote_id,
          lote_nombre: c.lote_nombre,
          parcela_numero: c.parcela_numero,
          frutas_maduras: c.frutas_maduras || 0,
          frutas_verdes: c.frutas_verdes || 0,
          estopas: c.estopas || 0,
          hojas_detras: c.hojas_detras || 0
        }));
      }
      
      // Preparar centros para Boyea
      let centros_boyeados = [];
      if (labor.labor_especifica === 'boyea' && labor.datos_boyea?.centros_boyeados) {
        centros_boyeados = labor.datos_boyea.centros_boyeados.map(c => ({
          centro_id: c.centro_id,
          centro_numero: c.centro_numero,
          lote_id: c.lote_id,
          lote_nombre: c.lote_nombre,
          parcela_numero: c.parcela_numero,
          frutas: c.frutas || 0
        }));
      }
      
      // Preparar datos para Ajilada
      let hora_inicio = '';
      let hora_salida = '';
      let horas_ordinarias = 0;
      let horas_extra = 0;
      let minutos_extra = 0;
      if (labor.labor_especifica === 'ajilada' && labor.datos_ajilada) {
        hora_inicio = labor.datos_ajilada.hora_inicio || '';
        hora_salida = labor.datos_ajilada.hora_salida || '';
        horas_ordinarias = labor.datos_ajilada.horas_ordinarias || 0;
        horas_extra = labor.datos_ajilada.horas_extra || 0;
        minutos_extra = labor.datos_ajilada.minutos_extra || 0;
      }
      
      // Preparar centros para Coyolea
      let centros_coyoleados = [];
      if (labor.labor_especifica === 'coyolea' && labor.datos_coyolea?.centros_coyoleados) {
        centros_coyoleados = labor.datos_coyolea.centros_coyoleados.map(c => ({
          centro_id: c.centro_id,
          centro_numero: c.centro_numero,
          lote_id: c.lote_id,
          lote_nombre: c.lote_nombre,
          parcela_numero: c.parcela_numero,
          sacos_coyol: c.sacos_coyol || 0,
          no_terminado: c.no_terminado || false
        }));
      }
      
      // Preparar centros para Alzada de Coyol
      let centros_alzados = [];
      if (labor.labor_especifica === 'alzada_coyol' && labor.datos_alzada_coyol?.centros_alzados) {
        centros_alzados = labor.datos_alzada_coyol.centros_alzados.map(c => ({
          centro_id: c.centro_id,
          centro_numero: c.centro_numero,
          lote_id: c.lote_id,
          lote_nombre: c.lote_nombre,
          parcela_numero: c.parcela_numero,
          sacos: c.sacos || 0,
          nota: c.nota || '',
          no_terminado: c.no_terminado || false
        }));
      }
      
      return {
        id: labor.id,
        labor_principal: labor.labor_principal,
        labor_especifica: labor.labor_especifica,
        cantidad: labor.cantidad || 0,
        unidad: labor.unidad || 'unidades',
        horas_trabajadas: labor.horas_trabajadas || 0,
        observaciones: labor.observaciones || '',
        centros_cortados,
        centros_boyeados,
        centros_coyoleados,
        centros_alzados,
        // Datos de Ajilada
        hora_inicio,
        hora_salida,
        horas_ordinarias,
        horas_extra,
        minutos_extra,
        // Datos de cortero vinculado para Ajilada
        cortero_vinculado_id: labor.datos_ajilada?.cortero_vinculado_id || '',
        cortero_vinculado_nombre: labor.datos_ajilada?.cortero_vinculado_nombre || '',
        frutas_ajiladas: labor.datos_ajilada?.frutas_ajiladas || 0,
        // Para agregar nuevos centros
        lote_id: '',
        parcela_id: ''
      };
    });
    
    setEditarTrabajador({ id: trabajadorId, nombre: trabajadorNombre });
    setLaboresEditables(laboresParaEditar);
    setLaborEditExpandida(0);
  };

  // Actualizar campo de una labor editable
  const handleUpdateLaborEditable = (laborIndex, field, value) => {
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return { ...labor, [field]: value };
    }));
  };

  // Actualizar campo de un centro de Corta
  const handleUpdateCentroCorta = (laborIndex, centroIndex, field, value) => {
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return {
        ...labor,
        centros_cortados: labor.centros_cortados.map((c, ci) => {
          if (ci !== centroIndex) return c;
          return { ...c, [field]: value };
        })
      };
    }));
  };

  // Actualizar campo de un centro de Boyea
  const handleUpdateCentroBoyea = (laborIndex, centroIndex, field, value) => {
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return {
        ...labor,
        centros_boyeados: labor.centros_boyeados.map((c, ci) => {
          if (ci !== centroIndex) return c;
          return { ...c, [field]: value };
        })
      };
    }));
  };

  // Eliminar un centro de Corta
  const handleEliminarCentroEditCorta = (laborIndex, centroIndex) => {
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return {
        ...labor,
        centros_cortados: labor.centros_cortados.filter((_, ci) => ci !== centroIndex)
      };
    }));
  };

  // Eliminar un centro de Boyea
  const handleEliminarCentroEditBoyea = (laborIndex, centroIndex) => {
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return {
        ...labor,
        centros_boyeados: labor.centros_boyeados.filter((_, ci) => ci !== centroIndex)
      };
    }));
  };

  // Manejar cambio de lote en modo edición
  const handleUpdateLoteEdit = (laborIndex, loteId) => {
    if (loteId) {
      fetchParcelasPorLote(loteId);
    }
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return { ...labor, lote_id: loteId, parcela_id: '' };
    }));
    // Limpiar formularios de nuevo centro
    setNuevoCentroEdit({ centro_id: '', frutas_maduras: '', frutas_verdes: '', estopas: '', hojas_detras: '' });
    setNuevoCentroBoyeaEdit({ centro_id: '', frutas: '' });
    setNuevoCentroCoyoleaEdit({ centro_id: '', sacos_coyol: '' });
    setNuevoCentroAlzadoEdit({ centro_id: '', sacos: '', nota: '' });
  };

  // Manejar cambio de parcela en modo edición
  const handleUpdateParcelaEdit = (laborIndex, parcelaId) => {
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return { ...labor, parcela_id: parcelaId };
    }));
    // Limpiar formularios de nuevo centro
    setNuevoCentroEdit({ centro_id: '', frutas_maduras: '', frutas_verdes: '', estopas: '', hojas_detras: '' });
    setNuevoCentroBoyeaEdit({ centro_id: '', frutas: '' });
    setNuevoCentroCoyoleaEdit({ centro_id: '', sacos_coyol: '' });
    setNuevoCentroAlzadoEdit({ centro_id: '', sacos: '', nota: '' });
  };

  // Agregar un centro cortado en modo edición
  const handleAgregarCentroEditCorta = (laborIndex) => {
    const labor = laboresEditables[laborIndex];
    if (!nuevoCentroEdit.centro_id) {
      toast.error('Selecciona un centro frutero');
      return;
    }
    
    // Verificar que no esté duplicado
    if (labor.centros_cortados.some(c => c.centro_id === nuevoCentroEdit.centro_id)) {
      toast.error('Este centro ya fue agregado');
      return;
    }
    
    // Obtener info del centro y lote
    const parcelaNumero = parseInt(labor.parcela_id.split('_parcela_')[1]);
    const centros = getCentrosPorParcela(labor.lote_id, parcelaNumero);
    const centroInfo = centros.find(c => c.id === nuevoCentroEdit.centro_id);
    const loteInfo = lotes.find(l => l.id === labor.lote_id);
    
    const nuevoCentroCompleto = {
      ...nuevoCentroEdit,
      centro_numero: centroInfo?.numero_centro || centroInfo?.numero || '?',
      lote_id: labor.lote_id,
      lote_nombre: loteInfo?.nombre || '',
      parcela_numero: parcelaNumero
    };
    
    setLaboresEditables(prev => prev.map((l, i) => {
      if (i !== laborIndex) return l;
      return {
        ...l,
        centros_cortados: [...l.centros_cortados, nuevoCentroCompleto]
      };
    }));
    
    // Limpiar formulario
    setNuevoCentroEdit({ centro_id: '', frutas_maduras: '', frutas_verdes: '', estopas: '', hojas_detras: '' });
    toast.success(`Centro ${nuevoCentroCompleto.centro_numero} agregado`);
  };

  // Agregar un centro boyeado en modo edición
  const handleAgregarCentroEditBoyea = (laborIndex) => {
    const labor = laboresEditables[laborIndex];
    if (!nuevoCentroBoyeaEdit.centro_id) {
      toast.error('Selecciona un centro frutero');
      return;
    }
    
    // Verificar que no esté duplicado
    if (labor.centros_boyeados.some(c => c.centro_id === nuevoCentroBoyeaEdit.centro_id)) {
      toast.error('Este centro ya fue agregado');
      return;
    }
    
    // Obtener info del centro y lote
    const parcelaNumero = parseInt(labor.parcela_id.split('_parcela_')[1]);
    const centros = getCentrosPorParcela(labor.lote_id, parcelaNumero);
    const centroInfo = centros.find(c => c.id === nuevoCentroBoyeaEdit.centro_id);
    const loteInfo = lotes.find(l => l.id === labor.lote_id);
    
    const nuevoCentroCompleto = {
      ...nuevoCentroBoyeaEdit,
      centro_numero: centroInfo?.numero_centro || centroInfo?.numero || '?',
      lote_id: labor.lote_id,
      lote_nombre: loteInfo?.nombre || '',
      parcela_numero: parcelaNumero
    };
    
    setLaboresEditables(prev => prev.map((l, i) => {
      if (i !== laborIndex) return l;
      return {
        ...l,
        centros_boyeados: [...l.centros_boyeados, nuevoCentroCompleto]
      };
    }));
    
    // Limpiar formulario
    setNuevoCentroBoyeaEdit({ centro_id: '', frutas: '' });
    toast.success(`Centro ${nuevoCentroCompleto.centro_numero} agregado`);
  };

  // Actualizar campo de un centro coyoleado
  const handleUpdateCentroCoyolea = (laborIndex, centroIndex, field, value) => {
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return {
        ...labor,
        centros_coyoleados: labor.centros_coyoleados.map((centro, ci) => {
          if (ci !== centroIndex) return centro;
          return { ...centro, [field]: value };
        })
      };
    }));
  };

  // Toggle "no_terminado" en centro coyoleado
  const handleToggleCentroNoTerminadoCoyolea = (laborIndex, centroIndex) => {
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return {
        ...labor,
        centros_coyoleados: labor.centros_coyoleados.map((centro, ci) => {
          if (ci !== centroIndex) return centro;
          return { ...centro, no_terminado: !centro.no_terminado };
        })
      };
    }));
  };

  // Eliminar un centro de Coyolea
  const handleEliminarCentroEditCoyolea = (laborIndex, centroIndex) => {
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return {
        ...labor,
        centros_coyoleados: labor.centros_coyoleados.filter((_, ci) => ci !== centroIndex)
      };
    }));
  };

  // Agregar un centro coyoleado en modo edición
  const handleAgregarCentroEditCoyolea = (laborIndex) => {
    const labor = laboresEditables[laborIndex];
    if (!nuevoCentroCoyoleaEdit.centro_id) {
      toast.error('Selecciona un centro frutero');
      return;
    }
    
    // Verificar que no esté duplicado
    if (labor.centros_coyoleados?.some(c => c.centro_id === nuevoCentroCoyoleaEdit.centro_id)) {
      toast.error('Este centro ya fue agregado');
      return;
    }
    
    // Obtener info del centro y lote
    const parcelaNumero = parseInt(labor.parcela_id.split('_parcela_')[1]);
    const centros = getCentrosPorParcela(labor.lote_id, parcelaNumero);
    const centroInfo = centros.find(c => c.id === nuevoCentroCoyoleaEdit.centro_id);
    const loteInfo = lotes.find(l => l.id === labor.lote_id);
    
    const nuevoCentroCompleto = {
      ...nuevoCentroCoyoleaEdit,
      centro_numero: centroInfo?.numero_centro || centroInfo?.numero || '?',
      lote_id: labor.lote_id,
      lote_nombre: loteInfo?.nombre || '',
      parcela_numero: parcelaNumero
    };
    
    setLaboresEditables(prev => prev.map((l, i) => {
      if (i !== laborIndex) return l;
      return {
        ...l,
        centros_coyoleados: [...(l.centros_coyoleados || []), nuevoCentroCompleto]
      };
    }));
    
    // Limpiar formulario
    setNuevoCentroCoyoleaEdit({ centro_id: '', sacos_coyol: '' });
    toast.success(`Centro ${nuevoCentroCompleto.centro_numero} agregado`);
  };

  // Actualizar campo de un centro alzado
  const handleUpdateCentroAlzado = (laborIndex, centroIndex, field, value) => {
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return {
        ...labor,
        centros_alzados: (labor.centros_alzados || []).map((centro, ci) => {
          if (ci !== centroIndex) return centro;
          return { ...centro, [field]: value };
        })
      };
    }));
  };

  // Toggle "no_terminado" en centro alzado
  const handleToggleCentroNoTerminadoAlzado = (laborIndex, centroIndex) => {
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return {
        ...labor,
        centros_alzados: (labor.centros_alzados || []).map((centro, ci) => {
          if (ci !== centroIndex) return centro;
          return { ...centro, no_terminado: !centro.no_terminado };
        })
      };
    }));
  };

  // Eliminar un centro alzado en modo edición
  const handleEliminarCentroEditAlzado = (laborIndex, centroIndex) => {
    setLaboresEditables(prev => prev.map((labor, i) => {
      if (i !== laborIndex) return labor;
      return {
        ...labor,
        centros_alzados: (labor.centros_alzados || []).filter((_, ci) => ci !== centroIndex)
      };
    }));
  };

  // Agregar un centro alzado en modo edición
  const handleAgregarCentroEditAlzado = (laborIndex) => {
    const labor = laboresEditables[laborIndex];
    if (!nuevoCentroAlzadoEdit.centro_id) {
      toast.error('Selecciona un centro frutero');
      return;
    }
    if (!nuevoCentroAlzadoEdit.sacos || parseFloat(nuevoCentroAlzadoEdit.sacos) < 0.5) {
      toast.error('Ingresa al menos 0.5 sacos');
      return;
    }
    
    // Verificar que no esté duplicado
    if ((labor.centros_alzados || []).some(c => c.centro_id === nuevoCentroAlzadoEdit.centro_id)) {
      toast.error('Este centro ya fue agregado');
      return;
    }
    
    // Obtener info del centro y lote
    const parcelaNumero = parseInt(labor.parcela_id.split('_parcela_')[1]);
    const centros = getCentrosPorParcela(labor.lote_id, parcelaNumero);
    const centroInfo = centros.find(c => c.id === nuevoCentroAlzadoEdit.centro_id);
    const loteInfo = lotes.find(l => l.id === labor.lote_id);
    
    const nuevoCentroCompleto = {
      ...nuevoCentroAlzadoEdit,
      centro_numero: centroInfo?.numero_centro || centroInfo?.numero || '?',
      lote_id: labor.lote_id,
      lote_nombre: loteInfo?.nombre || '',
      parcela_numero: parcelaNumero,
      sacos: parseFloat(nuevoCentroAlzadoEdit.sacos)
    };
    
    setLaboresEditables(prev => prev.map((l, i) => {
      if (i !== laborIndex) return l;
      return {
        ...l,
        centros_alzados: [...(l.centros_alzados || []), nuevoCentroCompleto]
      };
    }));
    
    // Limpiar formulario
    setNuevoCentroAlzadoEdit({ centro_id: '', sacos: '', nota: '' });
    toast.success(`Centro ${nuevoCentroCompleto.centro_numero} agregado`);
  };

  // Eliminar una labor completa del trabajador
  const handleEliminarLaborEdit = async (laborIndex) => {
    const labor = laboresEditables[laborIndex];
    if (!confirm('¿Eliminar esta labor?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/registros-labores/${registroActivo.id}/trabajadores/${labor.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const nuevasLabores = laboresEditables.filter((_, i) => i !== laborIndex);
      if (nuevasLabores.length === 0) {
        setEditarTrabajador(null);
        setLaboresEditables([]);
        toast.success('Labor eliminada. No quedan labores para este trabajador.');
      } else {
        setLaboresEditables(nuevasLabores);
        setLaborEditExpandida(Math.min(laborEditExpandida, nuevasLabores.length - 1));
        toast.success('Labor eliminada');
      }
      
      await fetchDetalleDia(registroActivo.id);
    } catch (error) {
      console.error('Error eliminando labor:', error);
      toast.error('Error al eliminar labor');
    }
  };

  // Guardar todos los cambios de edición
  const handleGuardarEdicion = async () => {
    setGuardandoEdicion(true);
    try {
      const token = localStorage.getItem('token');
      
      for (const labor of laboresEditables) {
        const payload = {
          cantidad: parseFloat(labor.cantidad) || 0,
          unidad: labor.unidad,
          horas_trabajadas: parseFloat(labor.horas_trabajadas) || 0,
          observaciones: labor.observaciones || null
        };
        
        // Datos de Corta
        if (labor.labor_especifica === 'corta') {
          payload.datos_corta = {
            centros_cortados: labor.centros_cortados.map(c => ({
              centro_id: c.centro_id,
              lote_id: c.lote_id,
              parcela_numero: c.parcela_numero,
              frutas_maduras: parseInt(c.frutas_maduras) || 0,
              frutas_verdes: parseInt(c.frutas_verdes) || 0,
              estopas: parseInt(c.estopas) || 0,
              hojas_detras: parseInt(c.hojas_detras) || 0,
              no_terminado: c.no_terminado || false
            }))
          };
        }
        
        // Datos de Boyea
        if (labor.labor_especifica === 'boyea') {
          payload.datos_boyea = {
            centros_boyeados: labor.centros_boyeados.map(c => ({
              centro_id: c.centro_id,
              lote_id: c.lote_id,
              parcela_numero: c.parcela_numero,
              frutas: parseInt(c.frutas) || 0,
              no_terminado: c.no_terminado || false
            }))
          };
        }
        
        // Datos de Ajilada
        if (labor.labor_especifica === 'ajilada') {
          payload.datos_ajilada = {
            hora_inicio: labor.hora_inicio,
            hora_salida: labor.hora_salida,
            horas_ordinarias: labor.horas_ordinarias || 0,
            horas_extra: labor.horas_extra || 0,
            minutos_extra: labor.minutos_extra || 0,
            cortero_vinculado_id: labor.cortero_vinculado_id || null,
            cortero_vinculado_nombre: labor.cortero_vinculado_nombre || null,
            frutas_ajiladas: labor.frutas_ajiladas || 0
          };
        }
        
        // Datos de Coyolea
        if (labor.labor_especifica === 'coyolea') {
          payload.datos_coyolea = {
            centros_coyoleados: labor.centros_coyoleados.map(c => ({
              centro_id: c.centro_id,
              lote_id: c.lote_id,
              parcela_numero: c.parcela_numero,
              sacos_coyol: parseInt(c.sacos_coyol) || 0,
              no_terminado: c.no_terminado || false
            }))
          };
        }
        
        // Datos de Alzada de Coyol
        if (labor.labor_especifica === 'alzada_coyol') {
          payload.datos_alzada_coyol = {
            centros_alzados: (labor.centros_alzados || []).map(c => ({
              centro_id: c.centro_id,
              lote_id: c.lote_id,
              parcela_numero: c.parcela_numero,
              sacos: parseFloat(c.sacos) || 0,
              nota: c.nota || null,
              no_terminado: c.no_terminado || false
            }))
          };
        }
        
        await axios.put(
          `${API}/registros-labores/${registroActivo.id}/trabajadores/${labor.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success('Cambios guardados exitosamente');
      setEditarTrabajador(null);
      setLaboresEditables([]);
      await fetchDetalleDia(registroActivo.id);
    } catch (error) {
      console.error('Error guardando cambios:', error);
      toast.error('Error al guardar cambios');
    } finally {
      setGuardandoEdicion(false);
    }
  };

  const handleCerrarDia = async () => {
    setCerrandoDia(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/registros-labores/${registroActivo.id}/cerrar`, {
        cerrado_por_id: user?.id || 'unknown',
        cerrado_por_nombre: user?.nombre || user?.username || 'Admin'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Día cerrado exitosamente');
      setShowConfirmCerrar(false);
      await fetchDetalleDia(registroActivo.id);
    } catch (error) {
      console.error('Error cerrando día:', error);
      toast.error(error.response?.data?.detail || 'Error al cerrar el día');
    } finally {
      setCerrandoDia(false);
    }
  };

  // VISTA DE LISTA DE REGISTROS
  if (vistaActual === 'lista') {
    return (
      <ProduccionLayout user={user} onLogout={onLogout}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <FileText className="w-7 h-7 text-emerald-600" />
                Registro de Labores
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Control diario de labores del personal
              </p>
            </div>

            <Button
              onClick={handleRegistrarDia}
              className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg"
              size="lg"
              data-testid="btn-registrar-dia"
            >
              <Plus className="w-5 h-5 mr-2" />
              Registrar Día
            </Button>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/30 border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">Total Registros</p>
                    <p className="text-3xl font-bold text-emerald-800 dark:text-emerald-200">
                      {estadisticas?.total_registros || 0}
                    </p>
                  </div>
                  <Calendar className="w-10 h-10 text-emerald-600/50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700 dark:text-blue-300">Este Mes</p>
                    <p className="text-3xl font-bold text-blue-800 dark:text-blue-200">
                      {estadisticas?.registros_mes_actual || 0}
                    </p>
                  </div>
                  <CalendarDays className="w-10 h-10 text-blue-600/50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-700 dark:text-purple-300">Último Registro</p>
                    <p className="text-lg font-bold text-purple-800 dark:text-purple-200">
                      {estadisticas?.ultimo_registro?.fecha 
                        ? formatFechaDisplay(estadisticas.ultimo_registro.fecha)
                        : 'Sin registros'}
                    </p>
                  </div>
                  <Clock className="w-10 h-10 text-purple-600/50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabla de registros */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-600" />
                Últimos Días Registrados
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                </div>
              ) : registros.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No hay registros aún
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    Comienza registrando el día de hoy
                  </p>
                  <Button onClick={handleRegistrarDia} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Registrar Primer Día
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[180px]">Día y Fecha</TableHead>
                        <TableHead className="text-center">Trabajadores</TableHead>
                        <TableHead>Periodo Planilla</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <AnimatePresence>
                        {registros.map((registro, index) => (
                          <motion.tr
                            key={registro.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                            onClick={() => handleVerDia(registro)}
                          >
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-emerald-600" />
                                <div>
                                  <p className="font-semibold text-gray-900 dark:text-white">
                                    {registro.dia_semana}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {formatFechaDisplay(registro.fecha)}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Users className="w-4 h-4 text-blue-600" />
                                <span className="font-semibold text-blue-700">
                                  {registro.trabajadores_registrados}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                <CalendarDays className="w-3 h-3 mr-1" />
                                {formatFechaDisplay(registro.periodo_planilla)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {registro.estado === 'activo' ? (
                                  <>
                                    <Badge className="bg-green-100 text-green-700">Activo</Badge>
                                    <p className="text-xs text-gray-500">
                                      Abierto por: <span className="font-medium">{registro.registrado_por_nombre}</span>
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <Badge variant="secondary" className="bg-gray-200 text-gray-600">
                                      <Lock className="w-3 h-3 mr-1" />
                                      Cerrado
                                    </Badge>
                                    <p className="text-xs text-gray-500">
                                      Abierto por: <span className="font-medium">{registro.registrado_por_nombre}</span>
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      Cerrado por: <span className="font-medium">
                                        {registro.cerrado_tipo === 'sistema' ? 'Sistema' : (registro.cerrado_por_nombre || 'N/A')}
                                      </span>
                                    </p>
                                  </>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVerDia(registro);
                                }}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Ver
                              </Button>
                            </TableCell>
                          </motion.tr>
                        ))}
                      </AnimatePresence>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dialog para crear nuevo día - Vista Lista */}
          <Dialog open={showCrearDia} onOpenChange={setShowCrearDia}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-600" />
                  Registrar Día
                </DialogTitle>
                <DialogDescription>
                  Selecciona la fecha del día a registrar y su periodo de planilla.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Fecha del día */}
                <div className="space-y-2">
                  <Label htmlFor="fecha-dia-lista">Fecha del Día *</Label>
                  <Input
                    id="fecha-dia-lista"
                    type="date"
                    value={formNuevoDia.fecha}
                    onChange={(e) => handleFechaChange(e.target.value)}
                    className="w-full"
                  />
                  {formNuevoDia.fecha && (
                    <p className="text-sm text-gray-500">
                      {getDiaSemana(`${formNuevoDia.fecha.split('-')[2]}-${formNuevoDia.fecha.split('-')[1]}-${formNuevoDia.fecha.split('-')[0]}`)}
                    </p>
                  )}
                </div>

                {/* Periodo de planilla */}
                <div className="space-y-2">
                  <Label>Periodo de Planilla (Fecha de Pago) *</Label>
                  {calendarioPlanillas.length === 0 && (
                    <p className="text-xs text-amber-600 mb-1">
                      Configure el calendario en Producción &gt; Planilla
                    </p>
                  )}
                  <Select
                    value={formNuevoDia.periodo_planilla}
                    onValueChange={(val) => setFormNuevoDia(prev => ({ ...prev, periodo_planilla: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar periodo de pago" />
                    </SelectTrigger>
                    <SelectContent>
                      {getOpcionesPeriodos().map((periodo) => (
                        <SelectItem key={periodo.value} value={periodo.value}>
                          {periodo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Resumen */}
                {formNuevoDia.fecha && formNuevoDia.periodo_planilla && (
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200 mb-2">
                      Resumen del registro:
                    </p>
                    <div className="space-y-1 text-sm text-emerald-700 dark:text-emerald-300">
                      <p>• Día: {getDiaSemana(`${formNuevoDia.fecha.split('-')[2]}-${formNuevoDia.fecha.split('-')[1]}-${formNuevoDia.fecha.split('-')[0]}`)}, {formatFechaDisplay(`${formNuevoDia.fecha.split('-')[2]}-${formNuevoDia.fecha.split('-')[1]}-${formNuevoDia.fecha.split('-')[0]}`)}</p>
                      <p>• Se pagará en: {formatFechaDisplay(`${formNuevoDia.periodo_planilla.split('-')[2]}-${formNuevoDia.periodo_planilla.split('-')[1]}-${formNuevoDia.periodo_planilla.split('-')[0]}`)}</p>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCrearDia(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={confirmarCrearDia}
                  disabled={creandoDia || !formNuevoDia.fecha || !formNuevoDia.periodo_planilla}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {creandoDia ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Crear Día
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </ProduccionLayout>
    );
  }

  // VISTA DE DETALLE DE DÍA
  return (
    <ProduccionLayout user={user} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loadingDetalle ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
          </div>
        ) : registroActivo ? (
          <>
            {/* Header del día */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={handleVolverLista} className="p-2">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {registroActivo.dia_semana}
                    </h1>
                    <Badge variant="outline" className="text-lg px-3 py-1">
                      {formatFechaDisplay(registroActivo.fecha)}
                    </Badge>
                    {registroActivo.estado === 'cerrado' && (
                      <Badge variant="secondary" className="bg-gray-200">
                        <Lock className="w-3 h-3 mr-1" />
                        Cerrado
                      </Badge>
                    )}
                  </div>
                  <p className="text-gray-500 mt-1">
                    Periodo Planilla: {formatFechaDisplay(registroActivo.periodo_planilla)}
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                {registroActivo.estado === 'activo' && (
                  <>
                    <Button
                      onClick={handleAbrirAgregarTrabajador}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="btn-agregar-trabajador"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Agregar Trabajador
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowConfirmCerrar(true)}
                      className="border-orange-300 text-orange-600 hover:bg-orange-50"
                      data-testid="btn-cerrar-dia"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Cerrar Día
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Resumen por labor */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Card de total trabajadores */}
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-700 dark:text-blue-300">Total Trabajadores</p>
                      <p className="text-4xl font-bold text-blue-800 dark:text-blue-200">
                        {new Set(trabajadoresDia.map(t => t.trabajador_id)).size}
                      </p>
                    </div>
                    <Users className="w-12 h-12 text-blue-600/50" />
                  </div>
                </CardContent>
              </Card>

              {/* Card de resumen por labor */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-600">Trabajadores por Labor</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(resumenLabores).length === 0 ? (
                    <p className="text-gray-400 text-sm">Sin trabajadores registrados</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(resumenLabores).map(([labor, data]) => (
                        <Badge key={labor} variant="outline" className="text-sm px-3 py-1">
                          <span className="capitalize">{labor}</span>
                          <span className="ml-2 bg-emerald-100 text-emerald-700 px-2 rounded-full">
                            {data.cantidad_trabajadores}
                          </span>
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* KPIs de Producción del Día */}
            {trabajadoresDia.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {/* Total Frutas Cortadas */}
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/30 border-amber-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">Frutas Cortadas</p>
                        <p className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                          {trabajadoresDia
                            .filter(t => t.labor_especifica === 'corta' && t.datos_corta?.centros_cortados)
                            .reduce((total, t) => 
                              total + t.datos_corta.centros_cortados.reduce((sum, c) => 
                                sum + (parseInt(c.frutas_maduras) || 0) + (parseInt(c.frutas_verdes) || 0), 0), 0)
                            .toLocaleString()}
                        </p>
                        <p className="text-[10px] text-amber-600 mt-1">
                          {trabajadoresDia
                            .filter(t => t.labor_especifica === 'corta' && t.datos_corta?.centros_cortados)
                            .reduce((total, t) => 
                              total + t.datos_corta.centros_cortados.reduce((sum, c) => sum + (parseInt(c.frutas_maduras) || 0), 0), 0)
                            .toLocaleString()} maduras · {trabajadoresDia
                            .filter(t => t.labor_especifica === 'corta' && t.datos_corta?.centros_cortados)
                            .reduce((total, t) => 
                              total + t.datos_corta.centros_cortados.reduce((sum, c) => sum + (parseInt(c.frutas_verdes) || 0), 0), 0)
                            .toLocaleString()} verdes
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-amber-200/50 flex items-center justify-center">
                        <span className="text-lg">🥥</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Total Frutas Boyeadas (Recolectadas) */}
                <Card className="bg-gradient-to-br from-sky-50 to-sky-100 dark:from-sky-900/30 border-sky-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-sky-700 dark:text-sky-300 font-medium">Frutas Recolectadas</p>
                        <p className="text-2xl font-bold text-sky-800 dark:text-sky-200">
                          {trabajadoresDia
                            .filter(t => t.labor_especifica === 'boyea' && t.datos_boyea?.centros_boyeados)
                            .reduce((total, t) => 
                              total + t.datos_boyea.centros_boyeados.reduce((sum, c) => sum + (parseInt(c.frutas) || 0), 0), 0)
                            .toLocaleString()}
                        </p>
                        <p className="text-[10px] text-sky-600 mt-1">
                          De {trabajadoresDia
                            .filter(t => t.labor_especifica === 'boyea' && t.datos_boyea?.centros_boyeados)
                            .reduce((total, t) => total + t.datos_boyea.centros_boyeados.length, 0)} centros boyeados
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-sky-200/50 flex items-center justify-center">
                        <span className="text-lg">🧺</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Total Sacos de Coyol */}
                <Card className="bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/30 border-teal-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-teal-700 dark:text-teal-300 font-medium">Sacos de Coyol</p>
                        <p className="text-2xl font-bold text-teal-800 dark:text-teal-200">
                          {trabajadoresDia
                            .filter(t => t.labor_especifica === 'coyolea' && t.datos_coyolea?.centros_coyoleados)
                            .reduce((total, t) => 
                              total + t.datos_coyolea.centros_coyoleados.reduce((sum, c) => sum + (parseInt(c.sacos_coyol) || 0), 0), 0)
                            .toLocaleString()}
                        </p>
                        <p className="text-[10px] text-teal-600 mt-1">
                          De {trabajadoresDia
                            .filter(t => t.labor_especifica === 'coyolea' && t.datos_coyolea?.centros_coyoleados)
                            .reduce((total, t) => total + t.datos_coyolea.centros_coyoleados.length, 0)} centros coyoleados
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-teal-200/50 flex items-center justify-center">
                        <span className="text-lg">🌴</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Total Sacos Alzados */}
                <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 border-orange-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-orange-700 dark:text-orange-300 font-medium">Sacos Alzados</p>
                        <p className="text-2xl font-bold text-orange-800 dark:text-orange-200">
                          {trabajadoresDia
                            .filter(t => t.labor_especifica === 'alzada_coyol' && t.datos_alzada_coyol?.centros_alzados)
                            .reduce((total, t) => 
                              total + t.datos_alzada_coyol.centros_alzados.reduce((sum, c) => sum + (parseFloat(c.sacos) || 0), 0), 0)
                            .toLocaleString()}
                        </p>
                        <p className="text-[10px] text-orange-600 mt-1">
                          De {trabajadoresDia
                            .filter(t => t.labor_especifica === 'alzada_coyol' && t.datos_alzada_coyol?.centros_alzados)
                            .reduce((total, t) => total + t.datos_alzada_coyol.centros_alzados.length, 0)} centros alzados
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-orange-200/50 flex items-center justify-center">
                        <span className="text-lg">📦</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Tabla de trabajadores del día */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-600" />
                  Trabajadores Registrados
                </CardTitle>
              </CardHeader>
              <CardContent>
                {trabajadoresDia.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No hay trabajadores registrados
                    </h3>
                    <p className="text-gray-500 mb-4">
                      Agrega trabajadores para este día
                    </p>
                    {registroActivo.estado === 'activo' && (
                      <Button onClick={handleAbrirAgregarTrabajador} className="bg-emerald-600 hover:bg-emerald-700">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Agregar Primer Trabajador
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Agrupar trabajadores por trabajador_id */}
                    {Object.entries(
                      trabajadoresDia.reduce((acc, labor) => {
                        const key = labor.trabajador_id;
                        if (!acc[key]) {
                          acc[key] = {
                            trabajador_nombre: labor.trabajador_nombre,
                            labores: []
                          };
                        }
                        acc[key].labores.push(labor);
                        return acc;
                      }, {})
                    ).map(([trabajadorId, data]) => (
                      <div key={trabajadorId} className="border rounded-lg overflow-hidden">
                        {/* Header del trabajador */}
                        <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-500" />
                            <span className="font-medium text-gray-900">{data.trabajador_nombre}</span>
                            <span className="text-xs text-gray-500">({data.labores.length} {data.labores.length === 1 ? 'labor' : 'labores'})</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Botón Ver */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => setVerTrabajador({ id: trabajadorId, nombre: data.trabajador_nombre, labores: data.labores })}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            
                            {/* Botón Editar - solo admin puede usarlo */}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={`h-7 w-7 p-0 ${isSuperAdmin ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50' : 'text-gray-400 cursor-not-allowed'}`}
                                      onClick={() => isSuperAdmin && registroActivo.estado === 'activo' && handleAbrirEdicion(trabajadorId, data.trabajador_nombre, data.labores)}
                                      disabled={!isSuperAdmin || registroActivo.estado !== 'activo'}
                                    >
                                      {isSuperAdmin ? (
                                        <Edit className="w-4 h-4" />
                                      ) : (
                                        <LockKeyhole className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                {!isSuperAdmin && (
                                  <TooltipContent side="top">
                                    <p className="text-xs">Solo el administrador puede editar este registro</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                            
                            {/* Botón Eliminar - solo admin puede usarlo */}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={`h-7 w-7 p-0 ${isSuperAdmin ? 'text-red-600 hover:text-red-700 hover:bg-red-50' : 'text-gray-400 cursor-not-allowed'}`}
                                      onClick={() => isSuperAdmin && registroActivo.estado === 'activo' && handleEliminarTrabajadorCompleto(trabajadorId, data.trabajador_nombre, data.labores)}
                                      disabled={!isSuperAdmin || registroActivo.estado !== 'activo'}
                                    >
                                      {isSuperAdmin ? (
                                        <Trash2 className="w-4 h-4" />
                                      ) : (
                                        <LockKeyhole className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                {!isSuperAdmin && (
                                  <TooltipContent side="top">
                                    <p className="text-xs">Solo el administrador puede eliminar este registro</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                        {/* Labores del trabajador */}
                        <div className="divide-y">
                          {data.labores.map((labor) => (
                            <div key={labor.id} className="px-4 py-2 flex items-center justify-between hover:bg-gray-50">
                              <div className="flex items-center gap-4 flex-1">
                                <div className="flex flex-col gap-0.5 min-w-[120px]">
                                  <Badge variant="outline" className="capitalize w-fit text-xs">
                                    {labor.labor_principal || labor.labor_tipo}
                                  </Badge>
                                  <span className="text-xs text-gray-600 capitalize">
                                    {labor.labor_especifica?.replace(/_/g, ' ') || '-'}
                                  </span>
                                </div>
                                {/* Info de centros para Corta/Boyea */}
                                {labor.labor_especifica === 'corta' && labor.datos_corta?.centros_cortados && (
                                  <div className="flex flex-wrap gap-1">
                                    {labor.datos_corta.centros_cortados.map((c, idx) => (
                                      <span key={idx} className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                        {c.lote_nombre?.substring(0, 3) || '?'}·C{c.centro_numero}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {labor.labor_especifica === 'boyea' && labor.datos_boyea?.centros_boyeados && (
                                  <div className="flex flex-wrap gap-1">
                                    {labor.datos_boyea.centros_boyeados.map((c, idx) => (
                                      <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                        {c.lote_nombre?.substring(0, 3) || '?'}·C{c.centro_numero}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {/* Cantidad para otras labores */}
                                {labor.labor_especifica !== 'corta' && labor.labor_especifica !== 'boyea' && labor.cantidad > 0 && (
                                  <span className="text-xs text-gray-600">{labor.cantidad} {labor.unidad}</span>
                                )}
                                {labor.horas_trabajadas > 0 && (
                                  <span className="text-xs text-gray-500">{labor.horas_trabajadas}h</span>
                                )}
                                {labor.observaciones && (
                                  <span className="text-xs text-gray-400 italic truncate max-w-[150px]">{labor.observaciones}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-center py-20">
            <AlertCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No se pudo cargar el registro</p>
            <Button variant="outline" onClick={handleVolverLista} className="mt-4">
              Volver a la lista
            </Button>
          </div>
        )}

        {/* Dialog para agregar trabajador */}
        <Dialog open={showAgregarTrabajador} onOpenChange={setShowAgregarTrabajador}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-emerald-600" />
                Agregar Trabajador
              </DialogTitle>
              <DialogDescription>
                {registroActivo && (
                  <span className="font-medium">
                    {registroActivo.dia_semana}, {formatFechaDisplay(registroActivo.fecha)}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Selector de trabajador */}
              <div className="space-y-2">
                <Label>Trabajador *</Label>
                <Select
                  value={formTrabajador.trabajador_id}
                  onValueChange={handleSeleccionarTrabajador}
                  disabled={loadingTrabajadores}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loadingTrabajadores ? "Cargando..." : "Seleccionar trabajador"} />
                  </SelectTrigger>
                  <SelectContent>
                    {trabajadoresActivos.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.nombre_completo} {t.puesto && `- ${t.puesto}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Lista de Labores */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Labores del día *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAgregarOtraLabor}
                    className="text-emerald-600 hover:text-emerald-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Agregar otra labor
                  </Button>
                </div>
                
                {formTrabajador.labores.map((labor, index) => (
                  <div key={index} className={`p-3 rounded-lg relative ${laborExpandida === index ? 'bg-gray-50' : 'bg-gray-100/50 border border-gray-200'}`}>
                    {/* Header de la labor - siempre clickeable */}
                    <div 
                      className={`flex items-center justify-between cursor-pointer ${laborExpandida !== index ? 'hover:bg-gray-200/50 rounded -m-3 p-3' : ''}`}
                      onClick={() => setLaborExpandida(laborExpandida === index ? -1 : index)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500">Labor {index + 1}</span>
                        {/* Resumen compacto cuando está colapsada */}
                        {laborExpandida !== index && labor.labor_especifica && (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-gray-400">•</span>
                            <span className="font-medium text-gray-700 capitalize">{labor.labor_especifica}</span>
                            {labor.labor_especifica === 'corta' && labor.centros_cortados.length > 0 && (
                              <span className="text-amber-600">({labor.centros_cortados.length} centros)</span>
                            )}
                            {labor.labor_especifica === 'boyea' && labor.centros_boyeados.length > 0 && (
                              <span className="text-blue-600">({labor.centros_boyeados.length} centros)</span>
                            )}
                            {labor.labor_especifica === 'ajilada' && labor.hora_inicio && labor.hora_salida && (
                              <span className="text-purple-600">({labor.hora_inicio} - {labor.hora_salida})</span>
                            )}
                            {labor.labor_especifica === 'coyolea' && (labor.centros_coyoleados || []).length > 0 && (
                              <span className="text-teal-600">({(labor.centros_coyoleados || []).length} centros, {(labor.centros_coyoleados || []).reduce((sum, c) => sum + (parseInt(c.sacos_coyol) || 0), 0)} sacos)</span>
                            )}
                            {labor.labor_especifica === 'alzada_coyol' && (labor.centros_alzados || []).length > 0 && (
                              <span className="text-orange-600">({(labor.centros_alzados || []).length} centros, {(labor.centros_alzados || []).reduce((sum, c) => sum + (parseFloat(c.sacos) || 0), 0)} sacos)</span>
                            )}
                            {labor.labor_especifica !== 'corta' && labor.labor_especifica !== 'boyea' && labor.labor_especifica !== 'ajilada' && labor.labor_especifica !== 'coyolea' && labor.labor_especifica !== 'alzada_coyol' && labor.cantidad && (
                              <span className="text-gray-500">{labor.cantidad} {labor.unidad}</span>
                            )}
                          </div>
                        )}
                        {/* Indicador si está sin completar */}
                        {laborExpandida !== index && !labor.labor_especifica && (
                          <span className="text-xs text-orange-500 italic">Sin completar</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {laborExpandida !== index ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        )}
                        {formTrabajador.labores.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                            onClick={(e) => { e.stopPropagation(); handleEliminarLaborForm(index); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Contenido expandido de la labor */}
                    {laborExpandida === index && (
                      <div className="space-y-3 mt-3">
                        {/* Labor Principal */}
                        <div className="space-y-1">
                          <Label className="text-xs">Labor Principal *</Label>
                          <Select
                            value={labor.labor_principal}
                            onValueChange={(val) => handleUpdateLabor(index, 'labor_principal', val)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Seleccionar labor principal" />
                            </SelectTrigger>
                            <SelectContent>
                              {LABORES_PRINCIPALES.map((tipo) => (
                                <SelectItem key={tipo.value} value={tipo.value}>
                                  {tipo.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Labor Específica - dependiente de Labor Principal */}
                        <div className="space-y-1">
                          <Label className="text-xs">Labor Específica *</Label>
                          <Select
                            value={labor.labor_especifica}
                            onValueChange={(val) => handleUpdateLabor(index, 'labor_especifica', val)}
                            disabled={!labor.labor_principal}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder={labor.labor_principal ? "Seleccionar labor específica" : "Primero seleccione labor principal"} />
                            </SelectTrigger>
                            <SelectContent>
                              {labor.labor_principal && LABORES_ESPECIFICAS[labor.labor_principal]?.map((tipo) => (
                                <SelectItem key={tipo.value} value={tipo.value}>
                                  {tipo.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                    {/* CAMPOS ESPECÍFICOS PARA LABOR "CORTA" */}
                    {labor.labor_especifica === 'corta' && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                        <p className="text-xs font-medium text-amber-700">Detalles de Corta</p>
                        
                        {/* Lote */}
                        <div className="space-y-1">
                          <Label className="text-xs">Lote *</Label>
                          <Select
                            value={labor.lote_id}
                            onValueChange={(val) => handleUpdateLabor(index, 'lote_id', val)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder={loadingLotes ? "Cargando..." : "Seleccionar lote"} />
                            </SelectTrigger>
                            <SelectContent>
                              {lotes.map((lote) => (
                                <SelectItem key={lote.id} value={lote.id}>
                                  {lote.nombre}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Parcela - dependiente del lote */}
                        <div className="space-y-1">
                          <Label className="text-xs">Parcela *</Label>
                          <Select
                            value={labor.parcela_id}
                            onValueChange={(val) => handleUpdateLabor(index, 'parcela_id', val)}
                            disabled={!labor.lote_id}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder={labor.lote_id ? "Seleccionar parcela" : "Primero seleccione lote"} />
                            </SelectTrigger>
                            <SelectContent>
                              {labor.lote_id && (parcelasPorLote[labor.lote_id] || []).map((parcela) => (
                                <SelectItem key={parcela.id} value={parcela.id}>
                                  Parcela {parcela.numero}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Lista de centros agregados */}
                        {labor.centros_cortados.length > 0 && (
                          <div className="space-y-2">
                            <Label className="text-xs font-medium text-amber-700">Centros agregados ({labor.centros_cortados.length})</Label>
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                              {labor.centros_cortados.map((centro, centroIdx) => (
                                <div key={centroIdx} className={`bg-white border rounded px-2 py-1.5 text-xs ${centro.no_terminado ? 'border-orange-400 bg-orange-50' : 'border-amber-300'}`}>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1">
                                      <span className="text-amber-600 text-[10px]">{centro.lote_nombre?.substring(0, 3) || '?'}</span>
                                      <span className="text-gray-400">·</span>
                                      <span className="font-medium">C{centro.centro_numero}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-600">
                                      <span>M:{centro.frutas_maduras || 0}</span>
                                      <span>V:{centro.frutas_verdes || 0}</span>
                                      <span>E:{centro.estopas || 0}</span>
                                      <span>H:{centro.hojas_detras || 0}</span>
                                      <button 
                                        type="button"
                                        onClick={() => handleEliminarCentroCortado(index, centroIdx)}
                                        className="text-red-500 hover:text-red-700 ml-1"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  {/* Checkbox No terminado */}
                                  <div className="flex items-center gap-2 mt-1 pt-1 border-t border-gray-100">
                                    <input
                                      type="checkbox"
                                      id={`no-terminado-corta-${index}-${centroIdx}`}
                                      checked={centro.no_terminado || false}
                                      onChange={() => handleToggleNoTerminadoCorta(index, centroIdx)}
                                      className="h-3.5 w-3.5 rounded border-orange-300 text-orange-500 focus:ring-orange-500"
                                    />
                                    <label 
                                      htmlFor={`no-terminado-corta-${index}-${centroIdx}`}
                                      className={`text-[10px] cursor-pointer ${centro.no_terminado ? 'text-orange-600 font-medium' : 'text-gray-500'}`}
                                    >
                                      No terminado (continúa mañana)
                                    </label>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Formulario para agregar nuevo centro */}
                        {labor.parcela_id && (
                          <div className="border border-dashed border-amber-400 rounded-lg p-2 space-y-2">
                            <Label className="text-xs font-medium text-amber-700">Agregar centro</Label>
                            
                            {/* Selector de centro */}
                            <Select
                              value={nuevoCentro.centro_id}
                              onValueChange={(val) => setNuevoCentro(prev => ({ ...prev, centro_id: val }))}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Seleccionar centro" />
                              </SelectTrigger>
                              <SelectContent>
                                {(() => {
                                  const parcelaNumero = parseInt(labor.parcela_id.split('_parcela_')[1]);
                                  const centros = getCentrosPorParcela(labor.lote_id, parcelaNumero);
                                  // Filtrar centros ya agregados
                                  const centrosDisponibles = centros.filter(
                                    c => !labor.centros_cortados.some(cc => cc.centro_id === c.id)
                                  );
                                  return centrosDisponibles.map((centro) => (
                                    <SelectItem key={centro.id} value={centro.id}>
                                      Centro {centro.numero_centro || centro.numero}
                                    </SelectItem>
                                  ));
                                })()}
                              </SelectContent>
                            </Select>

                            {/* Datos de producción en grid compacto */}
                            <div className="grid grid-cols-4 gap-1">
                              <div className="space-y-0.5">
                                <Label className="text-[10px] text-gray-500">Maduras</Label>
                                <Input
                                  type="number"
                                  placeholder="0"
                                  className="h-7 text-xs"
                                  value={nuevoCentro.frutas_maduras}
                                  onChange={(e) => setNuevoCentro(prev => ({ ...prev, frutas_maduras: e.target.value }))}
                                />
                              </div>
                              <div className="space-y-0.5">
                                <Label className="text-[10px] text-gray-500">Verdes</Label>
                                <Input
                                  type="number"
                                  placeholder="0"
                                  className="h-7 text-xs"
                                  value={nuevoCentro.frutas_verdes}
                                  onChange={(e) => setNuevoCentro(prev => ({ ...prev, frutas_verdes: e.target.value }))}
                                />
                              </div>
                              <div className="space-y-0.5">
                                <Label className="text-[10px] text-gray-500">Estopas</Label>
                                <Input
                                  type="number"
                                  placeholder="0"
                                  className="h-7 text-xs"
                                  value={nuevoCentro.estopas}
                                  onChange={(e) => setNuevoCentro(prev => ({ ...prev, estopas: e.target.value }))}
                                />
                              </div>
                              <div className="space-y-0.5">
                                <Label className="text-[10px] text-gray-500">Hojas</Label>
                                <Input
                                  type="number"
                                  placeholder="0"
                                  className="h-7 text-xs"
                                  value={nuevoCentro.hojas_detras}
                                  onChange={(e) => setNuevoCentro(prev => ({ ...prev, hojas_detras: e.target.value }))}
                                />
                              </div>
                            </div>

                            {/* Botón agregar centro */}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
                              onClick={() => handleAgregarCentroCortado(index)}
                              disabled={!nuevoCentro.centro_id}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Agregar Centro
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* CAMPOS ESPECÍFICOS PARA LABOR "BOYEA" */}
                    {labor.labor_especifica === 'boyea' && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                        <p className="text-xs font-medium text-blue-700">Detalles de Boyea</p>
                        
                        {/* Lote */}
                        <div className="space-y-1">
                          <Label className="text-xs">Lote *</Label>
                          <Select
                            value={labor.lote_id}
                            onValueChange={(val) => handleUpdateLabor(index, 'lote_id', val)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder={loadingLotes ? "Cargando..." : "Seleccionar lote"} />
                            </SelectTrigger>
                            <SelectContent>
                              {lotes.map((lote) => (
                                <SelectItem key={lote.id} value={lote.id}>
                                  {lote.nombre}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Parcela - dependiente del lote */}
                        <div className="space-y-1">
                          <Label className="text-xs">Parcela *</Label>
                          <Select
                            value={labor.parcela_id}
                            onValueChange={(val) => handleUpdateLabor(index, 'parcela_id', val)}
                            disabled={!labor.lote_id}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder={labor.lote_id ? "Seleccionar parcela" : "Primero seleccione lote"} />
                            </SelectTrigger>
                            <SelectContent>
                              {labor.lote_id && (parcelasPorLote[labor.lote_id] || []).map((parcela) => (
                                <SelectItem key={parcela.id} value={parcela.id}>
                                  Parcela {parcela.numero}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Lista de centros boyeados */}
                        {labor.centros_boyeados.length > 0 && (
                          <div className="space-y-2">
                            <Label className="text-xs font-medium text-blue-700">Centros agregados ({labor.centros_boyeados.length})</Label>
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                              {labor.centros_boyeados.map((centro, centroIdx) => (
                                <div key={centroIdx} className={`bg-white border rounded px-2 py-1.5 text-xs ${centro.no_terminado ? 'border-orange-400 bg-orange-50' : 'border-blue-300'}`}>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1">
                                      <span className="text-blue-600 text-[10px]">{centro.lote_nombre?.substring(0, 3) || '?'}</span>
                                      <span className="text-gray-400">·</span>
                                      <span className="font-medium">C{centro.centro_numero}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-600">
                                      <span>Frutas: {centro.frutas || 0}</span>
                                      <button 
                                        type="button"
                                        onClick={() => handleEliminarCentroBoyeado(index, centroIdx)}
                                        className="text-red-500 hover:text-red-700 ml-1"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  {/* Checkbox No terminado */}
                                  <div className="flex items-center gap-2 mt-1 pt-1 border-t border-gray-100">
                                    <input
                                      type="checkbox"
                                      id={`no-terminado-boyea-${index}-${centroIdx}`}
                                      checked={centro.no_terminado || false}
                                      onChange={() => handleToggleNoTerminadoBoyea(index, centroIdx)}
                                      className="h-3.5 w-3.5 rounded border-orange-300 text-orange-500 focus:ring-orange-500"
                                    />
                                    <label 
                                      htmlFor={`no-terminado-boyea-${index}-${centroIdx}`}
                                      className={`text-[10px] cursor-pointer ${centro.no_terminado ? 'text-orange-600 font-medium' : 'text-gray-500'}`}
                                    >
                                      No terminado (continúa mañana)
                                    </label>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Formulario para agregar nuevo centro */}
                        {labor.parcela_id && (
                          <div className="border border-dashed border-blue-400 rounded-lg p-2 space-y-2">
                            <Label className="text-xs font-medium text-blue-700">Agregar centro</Label>
                            
                            {/* Selector de centro y Frutas en la misma fila */}
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <Select
                                  value={nuevoCentroBoyea.centro_id}
                                  onValueChange={(val) => setNuevoCentroBoyea(prev => ({ ...prev, centro_id: val }))}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Seleccionar centro" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(() => {
                                      const parcelaNumero = parseInt(labor.parcela_id.split('_parcela_')[1]);
                                      const centros = getCentrosPorParcela(labor.lote_id, parcelaNumero);
                                      // Filtrar centros ya agregados
                                      const centrosDisponibles = centros.filter(
                                        c => !labor.centros_boyeados.some(cc => cc.centro_id === c.id)
                                      );
                                      return centrosDisponibles.map((centro) => (
                                        <SelectItem key={centro.id} value={centro.id}>
                                          Centro {centro.numero_centro || centro.numero}
                                        </SelectItem>
                                      ));
                                    })()}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="w-24">
                                <Input
                                  type="number"
                                  placeholder="Frutas"
                                  className="h-8 text-xs"
                                  value={nuevoCentroBoyea.frutas}
                                  onChange={(e) => setNuevoCentroBoyea(prev => ({ ...prev, frutas: e.target.value }))}
                                />
                              </div>
                            </div>

                            {/* Botón agregar centro */}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full h-7 text-xs border-blue-400 text-blue-700 hover:bg-blue-100"
                              onClick={() => handleAgregarCentroBoyeado(index)}
                              disabled={!nuevoCentroBoyea.centro_id}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Agregar Centro
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Formulario específico para Ajilada (pago por horas) */}
                    {labor.labor_especifica === 'ajilada' && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-3">
                        <p className="text-xs font-medium text-purple-700">Detalles de Ajilada (Pago por Horas)</p>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-purple-600">Hora Inicio</Label>
                            <Input
                              type="time"
                              className="h-9"
                              value={labor.hora_inicio || ''}
                              onChange={(e) => handleUpdateLabor(index, 'hora_inicio', e.target.value)}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-purple-600">Hora Salida</Label>
                            <Input
                              type="time"
                              className="h-9"
                              value={labor.hora_salida || ''}
                              onChange={(e) => handleUpdateLabor(index, 'hora_salida', e.target.value)}
                            />
                          </div>
                        </div>

                        {/* Resumen de horas calculadas */}
                        {labor.hora_inicio && labor.hora_salida && (
                          <div className="bg-white rounded-lg p-2 border border-purple-200">
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="flex justify-between">
                                <span className="text-purple-600">Horas Ordinarias:</span>
                                <span className="font-bold text-purple-800">
                                  {Math.floor(labor.horas_ordinarias || 0)}h {Math.round(((labor.horas_ordinarias || 0) % 1) * 60)}min
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-orange-600">Horas Extra:</span>
                                <span className="font-bold text-orange-700">
                                  {labor.horas_extra || 0}h {labor.minutos_extra || 0}min
                                </span>
                              </div>
                            </div>
                            <div className="mt-1 pt-1 border-t border-purple-100 text-center">
                              <span className="text-xs text-gray-500">
                                Total: {calcularHorasAjilada(labor.hora_inicio, labor.hora_salida).total_display}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Vincular a Cortero */}
                        <div className="space-y-2 pt-2 border-t border-purple-200">
                          <Label className="text-xs text-purple-600">Vincular a Cortero (opcional)</Label>
                          <Select
                            value={labor.cortero_vinculado_id || ''}
                            onValueChange={(val) => {
                              // Buscar el cortero seleccionado
                              const cortero = trabajadoresDia.find(t => t.id === val && t.labor_especifica === 'corta');
                              if (cortero) {
                                // Calcular frutas totales del cortero
                                const frutasTotales = cortero.datos_corta?.centros_cortados?.reduce((sum, c) => 
                                  sum + (parseInt(c.frutas_maduras) || 0) + (parseInt(c.frutas_verdes) || 0), 0) || 0;
                                handleUpdateLabor(index, 'cortero_vinculado_id', val);
                                handleUpdateLabor(index, 'cortero_vinculado_nombre', cortero.trabajador_nombre);
                                handleUpdateLabor(index, 'frutas_ajiladas', frutasTotales);
                              } else if (val === '') {
                                // Limpiar si se deselecciona
                                handleUpdateLabor(index, 'cortero_vinculado_id', '');
                                handleUpdateLabor(index, 'cortero_vinculado_nombre', '');
                                handleUpdateLabor(index, 'frutas_ajiladas', 0);
                              }
                            }}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Seleccionar cortero del día" />
                            </SelectTrigger>
                            <SelectContent>
                              {trabajadoresDia
                                .filter(t => t.labor_especifica === 'corta' && t.datos_corta?.centros_cortados?.length > 0)
                                .map(cortero => {
                                  const frutas = cortero.datos_corta.centros_cortados.reduce((sum, c) => 
                                    sum + (parseInt(c.frutas_maduras) || 0) + (parseInt(c.frutas_verdes) || 0), 0);
                                  return (
                                    <SelectItem key={cortero.id} value={cortero.id}>
                                      {cortero.trabajador_nombre} - {frutas} frutas cortadas
                                    </SelectItem>
                                  );
                                })}
                              {trabajadoresDia.filter(t => t.labor_especifica === 'corta' && t.datos_corta?.centros_cortados?.length > 0).length === 0 && (
                                <SelectItem value="no-corteros" disabled>No hay corteros registrados hoy</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          
                          {/* Mostrar info del cortero vinculado */}
                          {labor.cortero_vinculado_id && (
                            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                              <div className="flex items-center justify-between">
                                <span className="text-amber-700">
                                  Cortero: <span className="font-medium">{labor.cortero_vinculado_nombre}</span>
                                </span>
                                <span className="font-bold text-amber-800">
                                  {labor.frutas_ajiladas} frutas ajiladas
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Observaciones específicas para ajilada */}
                        {(labor.horas_extra > 0 || labor.minutos_extra > 0) && (
                          <div className="space-y-1">
                            <Label className="text-xs text-orange-600">
                              Observaciones / Razón por la que duró más de 8hrs
                            </Label>
                            <Input
                              placeholder="Ej: Mucha fruta, terreno difícil..."
                              className="h-9 border-orange-200 focus:border-orange-400"
                              value={labor.observaciones || ''}
                              onChange={(e) => handleUpdateLabor(index, 'observaciones', e.target.value)}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Formulario específico para Coyolea (sacos de coyol por centro) */}
                    {labor.labor_especifica === 'coyolea' && (
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-3">
                        <p className="text-xs font-medium text-teal-700">Centros para Coyolea</p>
                        
                        {/* Selector de Lote y Parcela */}
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs text-teal-600">Lote</Label>
                            <Select
                              value={labor.lote_id}
                              onValueChange={(val) => handleUpdateLabor(index, 'lote_id', val)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Seleccionar lote" />
                              </SelectTrigger>
                              <SelectContent>
                                {loadingLotes ? (
                                  <SelectItem value="loading" disabled>Cargando...</SelectItem>
                                ) : (
                                  lotes.map(lote => (
                                    <SelectItem key={lote.id} value={lote.id}>
                                      {lote.nombre} ({(parcelasPorLote[lote.id] || []).length} parcelas)
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-teal-600">Parcela</Label>
                            <Select
                              value={labor.parcela_id}
                              onValueChange={(val) => handleUpdateLabor(index, 'parcela_id', val)}
                              disabled={!labor.lote_id}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder={labor.lote_id ? "Seleccionar parcela" : "Primero selecciona lote"} />
                              </SelectTrigger>
                              <SelectContent>
                                {labor.lote_id && (parcelasPorLote[labor.lote_id] || []).map(parcela => (
                                  <SelectItem key={parcela.id} value={parcela.id}>
                                    Parcela {parcela.numero} ({getCentrosPorParcela(labor.lote_id, parcela.numero).length} centros)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Agregar nuevo centro coyoleado */}
                        {labor.lote_id && labor.parcela_id && (
                          <div className="bg-white rounded-lg p-2 border border-teal-200 space-y-2">
                            <p className="text-xs text-teal-600 font-medium">Agregar Centro</p>
                            <div className="grid grid-cols-12 gap-2">
                              <div className="col-span-4">
                                <Select
                                  value={nuevoCentroCoyolea.centro_id}
                                  onValueChange={(val) => setNuevoCentroCoyolea(prev => ({ ...prev, centro_id: val }))}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Centro" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getCentrosPorParcela(labor.lote_id, parseInt(labor.parcela_id.split('_parcela_')[1])).map(centro => (
                                      <SelectItem key={centro.id} value={centro.id}>
                                        Centro {centro.numero_centro || centro.numero}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-4">
                                <Input
                                  type="number"
                                  placeholder="Sacos"
                                  className="h-8 text-xs"
                                  value={nuevoCentroCoyolea.sacos_coyol}
                                  onChange={(e) => setNuevoCentroCoyolea(prev => ({ ...prev, sacos_coyol: e.target.value }))}
                                />
                              </div>
                              <div className="col-span-4">
                                <Button
                                  type="button"
                                  size="sm"
                                  className="h-8 w-full bg-teal-600 hover:bg-teal-700"
                                  onClick={() => handleAgregarCentroCoyoleado(index)}
                                  disabled={!nuevoCentroCoyolea.centro_id || !nuevoCentroCoyolea.sacos_coyol}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Agregar
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Lista de centros coyoleados */}
                        {(labor.centros_coyoleados || []).length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs text-teal-600">Centros agregados ({(labor.centros_coyoleados || []).length})</p>
                            <div className="max-h-40 overflow-y-auto space-y-1">
                              {(labor.centros_coyoleados || []).map((centro, cidx) => (
                                <div key={cidx} className="flex items-center justify-between bg-white p-2 rounded border border-teal-100 text-xs">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-teal-800">
                                      {centro.lote_nombre} · P{centro.parcela_numero} · Centro {centro.centro_numero}
                                    </span>
                                    <span className="text-teal-600 font-bold">
                                      {centro.sacos_coyol} sacos
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <label className="flex items-center gap-1 text-xs cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={centro.no_terminado || false}
                                        onChange={() => handleToggleNoTerminadoCoyolea(index, cidx)}
                                        className="h-3 w-3"
                                      />
                                      <span className="text-gray-500">No terminado</span>
                                    </label>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                      onClick={() => handleEliminarCentroCoyoleado(index, cidx)}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {/* Resumen de sacos */}
                            <div className="bg-teal-100 rounded p-2 text-xs text-center">
                              <span className="font-bold text-teal-800">
                                Total: {(labor.centros_coyoleados || []).reduce((sum, c) => sum + (parseInt(c.sacos_coyol) || 0), 0)} sacos de coyol
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Formulario específico para Alzada de Coyol */}
                    {labor.labor_especifica === 'alzada_coyol' && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-3">
                        <p className="text-xs font-medium text-orange-700">Centros para Alzada de Coyol</p>
                        
                        {/* Selector de Lote y Parcela */}
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs text-orange-600">Lote</Label>
                            <Select
                              value={labor.lote_id}
                              onValueChange={(val) => handleUpdateLabor(index, 'lote_id', val)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Seleccionar lote" />
                              </SelectTrigger>
                              <SelectContent>
                                {loadingLotes ? (
                                  <SelectItem value="loading" disabled>Cargando...</SelectItem>
                                ) : (
                                  lotes.map(lote => (
                                    <SelectItem key={lote.id} value={lote.id}>
                                      {lote.nombre} ({(parcelasPorLote[lote.id] || []).length} parcelas)
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-orange-600">Parcela</Label>
                            <Select
                              value={labor.parcela_id}
                              onValueChange={(val) => handleUpdateLabor(index, 'parcela_id', val)}
                              disabled={!labor.lote_id}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder={labor.lote_id ? "Seleccionar parcela" : "Primero selecciona lote"} />
                              </SelectTrigger>
                              <SelectContent>
                                {labor.lote_id && (parcelasPorLote[labor.lote_id] || []).map(parcela => (
                                  <SelectItem key={parcela.id} value={parcela.id}>
                                    Parcela {parcela.numero} ({getCentrosPorParcela(labor.lote_id, parcela.numero).length} centros)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Agregar nuevo centro alzado */}
                        {labor.lote_id && labor.parcela_id && (
                          <div className="bg-white rounded-lg p-2 border border-orange-200 space-y-2">
                            <p className="text-xs text-orange-600 font-medium">Agregar Centro</p>
                            <div className="grid grid-cols-12 gap-2">
                              <div className="col-span-3">
                                <Select
                                  value={nuevoCentroAlzado.centro_id}
                                  onValueChange={(val) => setNuevoCentroAlzado(prev => ({ ...prev, centro_id: val }))}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Centro" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getCentrosPorParcela(labor.lote_id, parseInt(labor.parcela_id.split('_parcela_')[1])).map(centro => (
                                      <SelectItem key={centro.id} value={centro.id}>
                                        Centro {centro.numero_centro || centro.numero}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-2">
                                <Input
                                  type="number"
                                  placeholder="Sacos"
                                  step="0.5"
                                  min="0.5"
                                  className="h-8 text-xs"
                                  value={nuevoCentroAlzado.sacos}
                                  onChange={(e) => setNuevoCentroAlzado(prev => ({ ...prev, sacos: e.target.value }))}
                                />
                              </div>
                              <div className="col-span-4">
                                <Select
                                  value={nuevoCentroAlzado.nota}
                                  onValueChange={(val) => setNuevoCentroAlzado(prev => ({ ...prev, nota: val === 'none' ? '' : val }))}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Nota (opcional)" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">Sin nota</SelectItem>
                                    <SelectItem value="Mucha basura">Mucha basura</SelectItem>
                                    <SelectItem value="Mucho coyol quemado">Mucho coyol quemado</SelectItem>
                                    <SelectItem value="Mucho coyol pasado">Mucho coyol pasado</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-3">
                                <Button
                                  type="button"
                                  size="sm"
                                  className="h-8 w-full bg-orange-600 hover:bg-orange-700"
                                  onClick={() => handleAgregarCentroAlzado(index)}
                                  disabled={!nuevoCentroAlzado.centro_id || !nuevoCentroAlzado.sacos}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Agregar
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Lista de centros alzados */}
                        {(labor.centros_alzados || []).length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs text-orange-600">Centros agregados ({(labor.centros_alzados || []).length})</p>
                            <div className="max-h-40 overflow-y-auto space-y-1">
                              {(labor.centros_alzados || []).map((centro, cidx) => (
                                <div key={cidx} className="flex items-center justify-between bg-white p-2 rounded border border-orange-100 text-xs">
                                  <div className="flex flex-col gap-0.5">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-orange-800">
                                        {centro.lote_nombre} · P{centro.parcela_numero} · Centro {centro.centro_numero}
                                      </span>
                                      <span className="text-orange-600 font-bold">
                                        {centro.sacos} sacos
                                      </span>
                                    </div>
                                    {centro.nota && (
                                      <span className="text-gray-500 text-[10px] italic">📝 {centro.nota}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <label className="flex items-center gap-1 text-xs cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={centro.no_terminado || false}
                                        onChange={() => handleToggleNoTerminadoAlzado(index, cidx)}
                                        className="h-3 w-3"
                                      />
                                      <span className="text-gray-500">No terminado</span>
                                    </label>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                      onClick={() => handleEliminarCentroAlzado(index, cidx)}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {/* Resumen de sacos */}
                            <div className="bg-orange-100 rounded p-2 text-xs text-center">
                              <span className="font-bold text-orange-800">
                                Total: {(labor.centros_alzados || []).reduce((sum, c) => sum + (parseFloat(c.sacos) || 0), 0)} sacos alzados
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Cantidad, unidad y horas en una fila - Solo si NO es Corta, Boyea, Ajilada, Coyolea ni Alzada Coyol */}
                    {labor.labor_especifica !== 'corta' && labor.labor_especifica !== 'boyea' && labor.labor_especifica !== 'ajilada' && labor.labor_especifica !== 'coyolea' && labor.labor_especifica !== 'alzada_coyol' && (
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Cantidad</Label>
                          <Input
                            type="number"
                            placeholder="0"
                            className="h-9"
                            value={labor.cantidad}
                            onChange={(e) => handleUpdateLabor(index, 'cantidad', e.target.value)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Unidad</Label>
                          <Select
                            value={labor.unidad}
                            onValueChange={(val) => handleUpdateLabor(index, 'unidad', val)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unidades">Unidades</SelectItem>
                              <SelectItem value="kilos">Kilos</SelectItem>
                              <SelectItem value="cajas">Cajas</SelectItem>
                              <SelectItem value="sacos">Sacos</SelectItem>
                              <SelectItem value="racimos">Racimos</SelectItem>
                              <SelectItem value="hectareas">Hectáreas</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Horas</Label>
                          <Input
                            type="number"
                            step="0.5"
                            placeholder="8"
                            className="h-9"
                            value={labor.horas_trabajadas}
                            onChange={(e) => handleUpdateLabor(index, 'horas_trabajadas', e.target.value)}
                          />
                        </div>
                      </div>
                    )}

                    {/* Observaciones */}
                    {laborExpandida === index && (
                      <div className="space-y-1">
                        <Label className="text-xs">Observaciones</Label>
                        <Input
                          placeholder="Notas adicionales..."
                          className="h-9"
                          value={labor.observaciones}
                          onChange={(e) => handleUpdateLabor(index, 'observaciones', e.target.value)}
                        />
                      </div>
                    )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAgregarTrabajador(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleGuardarTrabajador}
                disabled={guardandoTrabajador}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {guardandoTrabajador ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Guardar {formTrabajador.labores.filter(l => l.labor_principal && l.labor_especifica).length > 1 
                  ? `(${formTrabajador.labores.filter(l => l.labor_principal && l.labor_especifica).length} labores)` 
                  : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de confirmación para cerrar día */}
        <Dialog open={showConfirmCerrar} onOpenChange={setShowConfirmCerrar}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-600">
                <Lock className="w-5 h-5" />
                Cerrar Día
              </DialogTitle>
              <DialogDescription>
                ¿Estás seguro de cerrar este día? Una vez cerrado, no podrás agregar ni modificar trabajadores.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                <p className="font-medium text-orange-800 dark:text-orange-200">
                  {registroActivo?.dia_semana}, {formatFechaDisplay(registroActivo?.fecha)}
                </p>
                <p className="text-sm text-orange-600 mt-1">
                  {trabajadoresDia.length} trabajadores registrados
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirmCerrar(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleCerrarDia}
                disabled={cerrandoDia}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {cerrandoDia ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Lock className="w-4 h-4 mr-2" />
                )}
                Confirmar Cierre
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog para crear nuevo día */}
        <Dialog open={showCrearDia} onOpenChange={setShowCrearDia}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                Registrar Día
              </DialogTitle>
              <DialogDescription>
                Selecciona la fecha del día a registrar y su periodo de planilla.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Fecha del día */}
              <div className="space-y-2">
                <Label htmlFor="fecha-dia">Fecha del Día *</Label>
                <Input
                  id="fecha-dia"
                  type="date"
                  value={formNuevoDia.fecha}
                  onChange={(e) => handleFechaChange(e.target.value)}
                  className="w-full"
                />
                {formNuevoDia.fecha && (
                  <p className="text-sm text-gray-500">
                    {getDiaSemana(`${formNuevoDia.fecha.split('-')[2]}-${formNuevoDia.fecha.split('-')[1]}-${formNuevoDia.fecha.split('-')[0]}`)}
                  </p>
                )}
              </div>

              {/* Periodo de planilla */}
              <div className="space-y-2">
                <Label>Periodo de Planilla (Fecha de Pago) *</Label>
                {calendarioPlanillas.length === 0 && (
                  <p className="text-xs text-amber-600 mb-1">
                    Configure el calendario en Producción &gt; Planilla
                  </p>
                )}
                <Select
                  value={formNuevoDia.periodo_planilla}
                  onValueChange={(val) => setFormNuevoDia(prev => ({ ...prev, periodo_planilla: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar periodo de pago" />
                  </SelectTrigger>
                  <SelectContent>
                    {getOpcionesPeriodos().map((periodo) => (
                      <SelectItem key={periodo.value} value={periodo.value}>
                        {periodo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Resumen */}
              {formNuevoDia.fecha && formNuevoDia.periodo_planilla && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-200">
                  <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200 mb-2">
                    Resumen del registro:
                  </p>
                  <div className="space-y-1 text-sm text-emerald-700 dark:text-emerald-300">
                    <p>• Día: {getDiaSemana(`${formNuevoDia.fecha.split('-')[2]}-${formNuevoDia.fecha.split('-')[1]}-${formNuevoDia.fecha.split('-')[0]}`)}, {formatFechaDisplay(`${formNuevoDia.fecha.split('-')[2]}-${formNuevoDia.fecha.split('-')[1]}-${formNuevoDia.fecha.split('-')[0]}`)}</p>
                    <p>• Se pagará en: {formatFechaDisplay(`${formNuevoDia.periodo_planilla.split('-')[2]}-${formNuevoDia.periodo_planilla.split('-')[1]}-${formNuevoDia.periodo_planilla.split('-')[0]}`)}</p>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCrearDia(false)}>
                Cancelar
              </Button>
              <Button
                onClick={confirmarCrearDia}
                disabled={creandoDia || !formNuevoDia.fecha || !formNuevoDia.periodo_planilla}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {creandoDia ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Crear Día
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog para Ver Detalle de Trabajador */}
        <Dialog open={verTrabajador !== null} onOpenChange={(open) => !open && setVerTrabajador(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Reporte de Labores - {verTrabajador?.nombre}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {verTrabajador?.labores.map((labor, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize">
                        {labor.labor_principal}
                      </Badge>
                      <span className="text-sm font-medium capitalize">{labor.labor_especifica}</span>
                    </div>
                    {labor.horas_trabajadas > 0 && (
                      <span className="text-xs text-gray-500">{labor.horas_trabajadas}h</span>
                    )}
                  </div>
                  
                  {/* Detalles de Corta */}
                  {labor.labor_especifica === 'corta' && labor.datos_corta?.centros_cortados && (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs font-medium text-amber-700">Centros cortados:</p>
                      <div className="grid gap-1">
                        {labor.datos_corta.centros_cortados.map((c, cIdx) => (
                          <div key={cIdx} className="text-xs bg-white p-2 rounded border border-amber-200 flex justify-between">
                            <span className="font-medium">{c.lote_nombre} · Parcela {c.parcela_numero} · Centro {c.centro_numero}</span>
                            <span className="text-gray-600">M:{c.frutas_maduras} V:{c.frutas_verdes} E:{c.estopas} H:{c.hojas_detras}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Detalles de Boyea */}
                  {labor.labor_especifica === 'boyea' && labor.datos_boyea?.centros_boyeados && (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs font-medium text-blue-700">Centros boyeados:</p>
                      <div className="grid gap-1">
                        {labor.datos_boyea.centros_boyeados.map((c, cIdx) => (
                          <div key={cIdx} className="text-xs bg-white p-2 rounded border border-blue-200 flex justify-between">
                            <span className="font-medium">{c.lote_nombre} · Parcela {c.parcela_numero} · Centro {c.centro_numero}</span>
                            <span className="text-gray-600">Frutas: {c.frutas}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Detalles de Ajilada */}
                  {labor.labor_especifica === 'ajilada' && labor.datos_ajilada && (
                    <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded space-y-2">
                      <p className="text-xs font-medium text-purple-700">Detalle de horas:</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Entrada:</span>
                          <span className="font-medium">{labor.datos_ajilada.hora_inicio}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Salida:</span>
                          <span className="font-medium">{labor.datos_ajilada.hora_salida}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-purple-600">Horas Ordinarias:</span>
                          <span className="font-bold text-purple-800">
                            {Math.floor(labor.datos_ajilada.horas_ordinarias || 0)}h {Math.round(((labor.datos_ajilada.horas_ordinarias || 0) % 1) * 60)}min
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-orange-600">Horas Extra:</span>
                          <span className="font-bold text-orange-700">
                            {labor.datos_ajilada.horas_extra || 0}h {labor.datos_ajilada.minutos_extra || 0}min
                          </span>
                        </div>
                      </div>
                      {/* Cortero vinculado */}
                      {labor.datos_ajilada.cortero_vinculado_id && (
                        <div className="mt-2 pt-2 border-t border-purple-200">
                          <div className="bg-amber-50 border border-amber-200 rounded p-2">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-amber-700">
                                Cortero: <span className="font-medium">{labor.datos_ajilada.cortero_vinculado_nombre}</span>
                              </span>
                              <span className="font-bold text-amber-800">
                                {labor.datos_ajilada.frutas_ajiladas} frutas ajiladas
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Detalles de Coyolea */}
                  {labor.labor_especifica === 'coyolea' && labor.datos_coyolea?.centros_coyoleados && (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs font-medium text-teal-700">Centros coyoleados:</p>
                      <div className="grid gap-1">
                        {labor.datos_coyolea.centros_coyoleados.map((c, cIdx) => (
                          <div key={cIdx} className="text-xs bg-white p-2 rounded border border-teal-200 flex justify-between">
                            <span className="font-medium">{c.lote_nombre} · Parcela {c.parcela_numero} · Centro {c.centro_numero}</span>
                            <span className="text-teal-600 font-bold">{c.sacos_coyol} sacos</span>
                          </div>
                        ))}
                      </div>
                      <div className="bg-teal-100 rounded p-2 text-xs text-center mt-2">
                        <span className="font-bold text-teal-800">
                          Total: {labor.datos_coyolea.centros_coyoleados.reduce((sum, c) => sum + (parseInt(c.sacos_coyol) || 0), 0)} sacos de coyol
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Detalles de Alzada de Coyol */}
                  {labor.labor_especifica === 'alzada_coyol' && labor.datos_alzada_coyol?.centros_alzados && (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs font-medium text-orange-700">Centros alzados:</p>
                      <div className="grid gap-1">
                        {labor.datos_alzada_coyol.centros_alzados.map((c, cIdx) => (
                          <div key={cIdx} className="text-xs bg-white p-2 rounded border border-orange-200">
                            <div className="flex justify-between">
                              <span className="font-medium">{c.lote_nombre} · Parcela {c.parcela_numero} · Centro {c.centro_numero}</span>
                              <span className="text-orange-600 font-bold">{c.sacos} sacos</span>
                            </div>
                            {c.nota && (
                              <span className="text-gray-500 text-[10px] italic">📝 {c.nota}</span>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="bg-orange-100 rounded p-2 text-xs text-center mt-2">
                        <span className="font-bold text-orange-800">
                          Total: {labor.datos_alzada_coyol.centros_alzados.reduce((sum, c) => sum + (parseFloat(c.sacos) || 0), 0)} sacos alzados
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Otras labores con cantidad */}
                  {labor.labor_especifica !== 'corta' && labor.labor_especifica !== 'boyea' && labor.labor_especifica !== 'ajilada' && labor.labor_especifica !== 'coyolea' && labor.labor_especifica !== 'alzada_coyol' && labor.cantidad > 0 && (
                    <p className="text-sm text-gray-600">Cantidad: {labor.cantidad} {labor.unidad}</p>
                  )}
                  
                  {/* Observaciones */}
                  {labor.observaciones && (
                    <p className="text-xs text-gray-500 italic mt-1">"{labor.observaciones}"</p>
                  )}
                </div>
              ))}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setVerTrabajador(null)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog para Editar Trabajador */}
        <Dialog open={editarTrabajador !== null} onOpenChange={(open) => { if (!open) { setEditarTrabajador(null); setLaboresEditables([]); } }}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-amber-600" />
                Editar Labores - {editarTrabajador?.nombre}
              </DialogTitle>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto space-y-3 pr-2">
              {laboresEditables.map((labor, laborIdx) => (
                <div 
                  key={labor.id} 
                  className={`rounded-lg border ${laborEditExpandida === laborIdx ? 'bg-gray-50 border-amber-300' : 'bg-white border-gray-200'}`}
                >
                  {/* Header de la labor - clickeable */}
                  <div 
                    className="p-3 flex items-center justify-between cursor-pointer"
                    onClick={() => setLaborEditExpandida(laborEditExpandida === laborIdx ? -1 : laborIdx)}
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize text-xs">
                        {labor.labor_principal}
                      </Badge>
                      <span className="text-sm font-medium capitalize">{labor.labor_especifica}</span>
                      {labor.labor_especifica === 'corta' && (
                        <span className="text-xs text-amber-600">({labor.centros_cortados.length} centros)</span>
                      )}
                      {labor.labor_especifica === 'boyea' && (
                        <span className="text-xs text-blue-600">({labor.centros_boyeados.length} centros)</span>
                      )}
                      {labor.labor_especifica === 'ajilada' && labor.hora_inicio && labor.hora_salida && (
                        <span className="text-xs text-purple-600">({labor.hora_inicio} - {labor.hora_salida})</span>
                      )}
                      {labor.labor_especifica === 'coyolea' && (
                        <span className="text-xs text-teal-600">({labor.centros_coyoleados?.length || 0} centros)</span>
                      )}
                      {labor.labor_especifica === 'alzada_coyol' && (
                        <span className="text-xs text-orange-600">({labor.centros_alzados?.length || 0} centros)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {laborEditExpandida === laborIdx ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleEliminarLaborEdit(laborIdx); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Contenido expandido */}
                  {laborEditExpandida === laborIdx && (
                    <div className="px-3 pb-3 space-y-3 border-t border-gray-200 pt-3">
                      {/* Centros de Corta */}
                      {labor.labor_especifica === 'corta' && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-amber-700">Centros cortados:</p>
                          {labor.centros_cortados.map((centro, centroIdx) => (
                            <div key={centroIdx} className="bg-white border border-amber-200 rounded p-2 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">{centro.lote_nombre} · P{centro.parcela_numero} · C{centro.centro_numero}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 text-red-500 hover:text-red-700"
                                  onClick={() => handleEliminarCentroEditCorta(laborIdx, centroIdx)}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-4 gap-2">
                                <div>
                                  <Label className="text-[10px] text-gray-500">Maduras</Label>
                                  <Input
                                    type="number"
                                    className="h-7 text-xs"
                                    value={centro.frutas_maduras}
                                    onChange={(e) => handleUpdateCentroCorta(laborIdx, centroIdx, 'frutas_maduras', e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label className="text-[10px] text-gray-500">Verdes</Label>
                                  <Input
                                    type="number"
                                    className="h-7 text-xs"
                                    value={centro.frutas_verdes}
                                    onChange={(e) => handleUpdateCentroCorta(laborIdx, centroIdx, 'frutas_verdes', e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label className="text-[10px] text-gray-500">Estopas</Label>
                                  <Input
                                    type="number"
                                    className="h-7 text-xs"
                                    value={centro.estopas}
                                    onChange={(e) => handleUpdateCentroCorta(laborIdx, centroIdx, 'estopas', e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label className="text-[10px] text-gray-500">Hojas</Label>
                                  <Input
                                    type="number"
                                    className="h-7 text-xs"
                                    value={centro.hojas_detras}
                                    onChange={(e) => handleUpdateCentroCorta(laborIdx, centroIdx, 'hojas_detras', e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                          {labor.centros_cortados.length === 0 && (
                            <p className="text-xs text-gray-400 italic">No hay centros</p>
                          )}
                          
                          {/* Formulario para agregar nuevo centro de Corta */}
                          <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                            <p className="text-xs font-medium text-amber-800 flex items-center gap-1">
                              <Plus className="w-3 h-3" /> Agregar centro
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <Select value={labor.lote_id} onValueChange={(val) => handleUpdateLoteEdit(laborIdx, val)}>
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue placeholder="Lote" />
                                </SelectTrigger>
                                <SelectContent>
                                  {lotes.map(lote => (
                                    <SelectItem key={lote.id} value={lote.id}>{lote.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select 
                                value={labor.parcela_id} 
                                onValueChange={(val) => handleUpdateParcelaEdit(laborIdx, val)}
                                disabled={!labor.lote_id}
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue placeholder="Parcela" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(parcelasPorLote[labor.lote_id] || []).map(parcela => (
                                    <SelectItem key={parcela.id} value={parcela.id}>Parcela {parcela.numero}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {labor.lote_id && labor.parcela_id && (
                              <>
                                <Select 
                                  value={nuevoCentroEdit.centro_id} 
                                  onValueChange={(val) => setNuevoCentroEdit(prev => ({ ...prev, centro_id: val }))}
                                >
                                  <SelectTrigger className="h-7 text-xs">
                                    <SelectValue placeholder="Centro frutero" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(() => {
                                      const centros = getCentrosPorParcela(labor.lote_id, parseInt(labor.parcela_id.split('_parcela_')[1]));
                                      // Filtrar centros ya agregados
                                      const centrosDisponibles = centros.filter(
                                        c => !labor.centros_cortados.some(cc => cc.centro_id === c.id)
                                      );
                                      return centrosDisponibles.map(centro => (
                                        <SelectItem key={centro.id} value={centro.id}>
                                          Centro {centro.numero_centro || centro.numero}
                                        </SelectItem>
                                      ));
                                    })()}
                                  </SelectContent>
                                </Select>
                                {nuevoCentroEdit.centro_id && (
                                  <div className="grid grid-cols-4 gap-1">
                                    <Input type="number" className="h-7 text-xs" placeholder="Maduras"
                                      value={nuevoCentroEdit.frutas_maduras}
                                      onChange={(e) => setNuevoCentroEdit(prev => ({ ...prev, frutas_maduras: e.target.value }))}
                                    />
                                    <Input type="number" className="h-7 text-xs" placeholder="Verdes"
                                      value={nuevoCentroEdit.frutas_verdes}
                                      onChange={(e) => setNuevoCentroEdit(prev => ({ ...prev, frutas_verdes: e.target.value }))}
                                    />
                                    <Input type="number" className="h-7 text-xs" placeholder="Estopas"
                                      value={nuevoCentroEdit.estopas}
                                      onChange={(e) => setNuevoCentroEdit(prev => ({ ...prev, estopas: e.target.value }))}
                                    />
                                    <Input type="number" className="h-7 text-xs" placeholder="Hojas"
                                      value={nuevoCentroEdit.hojas_detras}
                                      onChange={(e) => setNuevoCentroEdit(prev => ({ ...prev, hojas_detras: e.target.value }))}
                                    />
                                  </div>
                                )}
                                <Button
                                  size="sm"
                                  className="w-full h-7 text-xs bg-amber-600 hover:bg-amber-700"
                                  onClick={() => handleAgregarCentroEditCorta(laborIdx)}
                                  disabled={!nuevoCentroEdit.centro_id}
                                >
                                  <Plus className="w-3 h-3 mr-1" /> Agregar Centro
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Centros de Boyea */}
                      {labor.labor_especifica === 'boyea' && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-blue-700">Centros boyeados:</p>
                          {labor.centros_boyeados.map((centro, centroIdx) => (
                            <div key={centroIdx} className="bg-white border border-blue-200 rounded p-2 flex items-center gap-2">
                              <span className="text-xs font-medium flex-1">{centro.lote_nombre} · P{centro.parcela_numero} · C{centro.centro_numero}</span>
                              <div className="w-20">
                                <Input
                                  type="number"
                                  className="h-7 text-xs"
                                  placeholder="Frutas"
                                  value={centro.frutas}
                                  onChange={(e) => handleUpdateCentroBoyea(laborIdx, centroIdx, 'frutas', e.target.value)}
                                />
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 w-5 p-0 text-red-500 hover:text-red-700"
                                onClick={() => handleEliminarCentroEditBoyea(laborIdx, centroIdx)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                          {labor.centros_boyeados.length === 0 && (
                            <p className="text-xs text-gray-400 italic">No hay centros</p>
                          )}
                          
                          {/* Formulario para agregar nuevo centro de Boyea */}
                          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                            <p className="text-xs font-medium text-blue-800 flex items-center gap-1">
                              <Plus className="w-3 h-3" /> Agregar centro
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <Select value={labor.lote_id} onValueChange={(val) => handleUpdateLoteEdit(laborIdx, val)}>
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue placeholder="Lote" />
                                </SelectTrigger>
                                <SelectContent>
                                  {lotes.map(lote => (
                                    <SelectItem key={lote.id} value={lote.id}>{lote.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select 
                                value={labor.parcela_id} 
                                onValueChange={(val) => handleUpdateParcelaEdit(laborIdx, val)}
                                disabled={!labor.lote_id}
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue placeholder="Parcela" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(parcelasPorLote[labor.lote_id] || []).map(parcela => (
                                    <SelectItem key={parcela.id} value={parcela.id}>Parcela {parcela.numero}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {labor.lote_id && labor.parcela_id && (
                              <>
                                <div className="flex gap-2">
                                  <Select 
                                    value={nuevoCentroBoyeaEdit.centro_id} 
                                    onValueChange={(val) => setNuevoCentroBoyeaEdit(prev => ({ ...prev, centro_id: val }))}
                                  >
                                    <SelectTrigger className="h-7 text-xs flex-1">
                                      <SelectValue placeholder="Centro frutero" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(() => {
                                        const centros = getCentrosPorParcela(labor.lote_id, parseInt(labor.parcela_id.split('_parcela_')[1]));
                                        // Filtrar centros ya agregados
                                        const centrosDisponibles = centros.filter(
                                          c => !labor.centros_boyeados.some(cc => cc.centro_id === c.id)
                                        );
                                        return centrosDisponibles.map(centro => (
                                          <SelectItem key={centro.id} value={centro.id}>
                                            Centro {centro.numero_centro || centro.numero}
                                          </SelectItem>
                                        ));
                                      })()}
                                    </SelectContent>
                                  </Select>
                                  <Input 
                                    type="number" 
                                    className="h-7 text-xs w-20" 
                                    placeholder="Frutas"
                                    value={nuevoCentroBoyeaEdit.frutas}
                                    onChange={(e) => setNuevoCentroBoyeaEdit(prev => ({ ...prev, frutas: e.target.value }))}
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  className="w-full h-7 text-xs bg-blue-600 hover:bg-blue-700"
                                  onClick={() => handleAgregarCentroEditBoyea(laborIdx)}
                                  disabled={!nuevoCentroBoyeaEdit.centro_id}
                                >
                                  <Plus className="w-3 h-3 mr-1" /> Agregar Centro
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Campos para Ajilada (pago por horas) */}
                      {labor.labor_especifica === 'ajilada' && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-3">
                          <p className="text-xs font-medium text-purple-700">Editar horas de Ajilada</p>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-purple-600">Hora Inicio</Label>
                              <Input
                                type="time"
                                className="h-8"
                                value={labor.hora_inicio || ''}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  const newLabor = { ...labor, hora_inicio: newValue };
                                  const calculo = calcularHorasAjilada(newValue, labor.hora_salida);
                                  handleUpdateLaborEditable(laborIdx, 'hora_inicio', newValue);
                                  handleUpdateLaborEditable(laborIdx, 'horas_ordinarias', calculo.horas_ordinarias);
                                  handleUpdateLaborEditable(laborIdx, 'horas_extra', calculo.horas_extra);
                                  handleUpdateLaborEditable(laborIdx, 'minutos_extra', calculo.minutos_extra);
                                }}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-purple-600">Hora Salida</Label>
                              <Input
                                type="time"
                                className="h-8"
                                value={labor.hora_salida || ''}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  const calculo = calcularHorasAjilada(labor.hora_inicio, newValue);
                                  handleUpdateLaborEditable(laborIdx, 'hora_salida', newValue);
                                  handleUpdateLaborEditable(laborIdx, 'horas_ordinarias', calculo.horas_ordinarias);
                                  handleUpdateLaborEditable(laborIdx, 'horas_extra', calculo.horas_extra);
                                  handleUpdateLaborEditable(laborIdx, 'minutos_extra', calculo.minutos_extra);
                                }}
                              />
                            </div>
                          </div>

                          {/* Resumen de horas calculadas */}
                          {labor.hora_inicio && labor.hora_salida && (
                            <div className="bg-white rounded-lg p-2 border border-purple-200">
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-purple-600">Horas Ordinarias:</span>
                                  <span className="font-bold text-purple-800">
                                    {Math.floor(labor.horas_ordinarias || 0)}h {Math.round(((labor.horas_ordinarias || 0) % 1) * 60)}min
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-orange-600">Horas Extra:</span>
                                  <span className="font-bold text-orange-700">
                                    {labor.horas_extra || 0}h {labor.minutos_extra || 0}min
                                  </span>
                                </div>
                              </div>
                              <div className="mt-1 pt-1 border-t border-purple-100 text-center">
                                <span className="text-xs text-gray-500">
                                  Total: {calcularHorasAjilada(labor.hora_inicio, labor.hora_salida).total_display}
                                </span>
                              </div>
                            </div>
                          )}
                          
                          {/* Vincular a Cortero */}
                          <div className="space-y-2 pt-2 border-t border-purple-200">
                            <Label className="text-xs text-purple-600">Vincular a Cortero (opcional)</Label>
                            <Select
                              value={labor.cortero_vinculado_id || ''}
                              onValueChange={(val) => {
                                const cortero = trabajadoresDia.find(t => t.id === val && t.labor_especifica === 'corta');
                                if (cortero) {
                                  const frutasTotales = cortero.datos_corta?.centros_cortados?.reduce((sum, c) => 
                                    sum + (parseInt(c.frutas_maduras) || 0) + (parseInt(c.frutas_verdes) || 0), 0) || 0;
                                  handleUpdateLaborEditable(laborIdx, 'cortero_vinculado_id', val);
                                  handleUpdateLaborEditable(laborIdx, 'cortero_vinculado_nombre', cortero.trabajador_nombre);
                                  handleUpdateLaborEditable(laborIdx, 'frutas_ajiladas', frutasTotales);
                                } else if (val === '') {
                                  handleUpdateLaborEditable(laborIdx, 'cortero_vinculado_id', '');
                                  handleUpdateLaborEditable(laborIdx, 'cortero_vinculado_nombre', '');
                                  handleUpdateLaborEditable(laborIdx, 'frutas_ajiladas', 0);
                                }
                              }}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Seleccionar cortero del día" />
                              </SelectTrigger>
                              <SelectContent>
                                {trabajadoresDia
                                  .filter(t => t.labor_especifica === 'corta' && t.datos_corta?.centros_cortados?.length > 0)
                                  .map(cortero => {
                                    const frutas = cortero.datos_corta.centros_cortados.reduce((sum, c) => 
                                      sum + (parseInt(c.frutas_maduras) || 0) + (parseInt(c.frutas_verdes) || 0), 0);
                                    return (
                                      <SelectItem key={cortero.id} value={cortero.id}>
                                        {cortero.trabajador_nombre} - {frutas} frutas
                                      </SelectItem>
                                    );
                                  })}
                                {trabajadoresDia.filter(t => t.labor_especifica === 'corta' && t.datos_corta?.centros_cortados?.length > 0).length === 0 && (
                                  <SelectItem value="no-corteros" disabled>No hay corteros registrados hoy</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                            
                            {labor.cortero_vinculado_id && (
                              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                                <div className="flex items-center justify-between">
                                  <span className="text-amber-700">
                                    Cortero: <span className="font-medium">{labor.cortero_vinculado_nombre}</span>
                                  </span>
                                  <span className="font-bold text-amber-800">
                                    {labor.frutas_ajiladas} frutas ajiladas
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Observaciones para horas extra */}
                          {(labor.horas_extra > 0 || labor.minutos_extra > 0) && (
                            <div className="space-y-1">
                              <Label className="text-xs text-orange-600">
                                Observaciones / Razón por la que duró más de 8hrs
                              </Label>
                              <Input
                                placeholder="Ej: Mucha fruta, terreno difícil..."
                                className="h-8 border-orange-200 focus:border-orange-400"
                                value={labor.observaciones || ''}
                                onChange={(e) => handleUpdateLaborEditable(laborIdx, 'observaciones', e.target.value)}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Campos para Coyolea (sacos de coyol por centro) */}
                      {labor.labor_especifica === 'coyolea' && (
                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-3">
                          <p className="text-xs font-medium text-teal-700">Centros coyoleados:</p>
                          {labor.centros_coyoleados?.map((centro, centroIdx) => (
                            <div key={centroIdx} className="bg-white border border-teal-200 rounded p-2 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">{centro.lote_nombre} · P{centro.parcela_numero} · C{centro.centro_numero}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 text-red-500 hover:text-red-700"
                                  onClick={() => handleEliminarCentroEditCoyolea(laborIdx, centroIdx)}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex-1">
                                  <Label className="text-[10px] text-gray-500">Sacos de coyol</Label>
                                  <Input
                                    type="number"
                                    className="h-7 text-xs"
                                    value={centro.sacos_coyol}
                                    onChange={(e) => handleUpdateCentroCoyolea(laborIdx, centroIdx, 'sacos_coyol', e.target.value)}
                                  />
                                </div>
                                <label className="flex items-center gap-1 text-xs cursor-pointer mt-4">
                                  <input
                                    type="checkbox"
                                    checked={centro.no_terminado || false}
                                    onChange={() => handleToggleCentroNoTerminadoCoyolea(laborIdx, centroIdx)}
                                    className="h-3 w-3"
                                  />
                                  <span className="text-gray-500">No terminado</span>
                                </label>
                              </div>
                            </div>
                          ))}
                          {(!labor.centros_coyoleados || labor.centros_coyoleados.length === 0) && (
                            <p className="text-xs text-gray-400 italic">No hay centros</p>
                          )}
                          
                          {/* Formulario para agregar nuevo centro de Coyolea */}
                          <div className="mt-3 p-2 bg-teal-50 border border-teal-200 rounded-lg space-y-2">
                            <p className="text-xs font-medium text-teal-800 flex items-center gap-1">
                              <Plus className="w-3 h-3" /> Agregar centro
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <Select value={labor.lote_id} onValueChange={(val) => handleUpdateLoteEdit(laborIdx, val)}>
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue placeholder="Lote" />
                                </SelectTrigger>
                                <SelectContent>
                                  {lotes.map(lote => (
                                    <SelectItem key={lote.id} value={lote.id}>{lote.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select 
                                value={labor.parcela_id} 
                                onValueChange={(val) => handleUpdateParcelaEdit(laborIdx, val)}
                                disabled={!labor.lote_id}
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue placeholder="Parcela" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(parcelasPorLote[labor.lote_id] || []).map(parcela => (
                                    <SelectItem key={parcela.id} value={parcela.id}>Parcela {parcela.numero}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {labor.lote_id && labor.parcela_id && (
                              <>
                                <div className="flex gap-2">
                                  <Select 
                                    value={nuevoCentroCoyoleaEdit.centro_id} 
                                    onValueChange={(val) => setNuevoCentroCoyoleaEdit(prev => ({ ...prev, centro_id: val }))}
                                  >
                                    <SelectTrigger className="h-7 text-xs flex-1">
                                      <SelectValue placeholder="Centro frutero" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(() => {
                                        const centros = getCentrosPorParcela(labor.lote_id, parseInt(labor.parcela_id.split('_parcela_')[1]));
                                        // Filtrar centros ya agregados
                                        const centrosDisponibles = centros.filter(
                                          c => !labor.centros_coyoleados?.some(cc => cc.centro_id === c.id)
                                        );
                                        return centrosDisponibles.map(centro => (
                                          <SelectItem key={centro.id} value={centro.id}>
                                            Centro {centro.numero_centro || centro.numero}
                                          </SelectItem>
                                        ));
                                      })()}
                                    </SelectContent>
                                  </Select>
                                  <Input 
                                    type="number" 
                                    className="h-7 text-xs w-20" 
                                    placeholder="Sacos"
                                    value={nuevoCentroCoyoleaEdit.sacos_coyol}
                                    onChange={(e) => setNuevoCentroCoyoleaEdit(prev => ({ ...prev, sacos_coyol: e.target.value }))}
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  className="w-full h-7 text-xs bg-teal-600 hover:bg-teal-700"
                                  onClick={() => handleAgregarCentroEditCoyolea(laborIdx)}
                                  disabled={!nuevoCentroCoyoleaEdit.centro_id}
                                >
                                  <Plus className="w-3 h-3 mr-1" /> Agregar Centro
                                </Button>
                              </>
                            )}
                          </div>
                          
                          {/* Resumen de sacos */}
                          {(labor.centros_coyoleados || []).length > 0 && (
                            <div className="bg-teal-100 rounded p-2 text-xs text-center">
                              <span className="font-bold text-teal-800">
                                Total: {(labor.centros_coyoleados || []).reduce((sum, c) => sum + (parseInt(c.sacos_coyol) || 0), 0)} sacos de coyol
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Campos para Alzada de Coyol */}
                      {labor.labor_especifica === 'alzada_coyol' && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-3">
                          <p className="text-xs font-medium text-orange-700">Centros alzados:</p>
                          {(labor.centros_alzados || []).map((centro, centroIdx) => (
                            <div key={centroIdx} className="bg-white border border-orange-200 rounded p-2 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">{centro.lote_nombre} · P{centro.parcela_numero} · C{centro.centro_numero}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 text-red-500 hover:text-red-700"
                                  onClick={() => handleEliminarCentroEditAlzado(laborIdx, centroIdx)}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex-1">
                                  <Label className="text-[10px] text-gray-500">Sacos</Label>
                                  <Input
                                    type="number"
                                    step="0.5"
                                    min="0.5"
                                    className="h-7 text-xs"
                                    value={centro.sacos}
                                    onChange={(e) => handleUpdateCentroAlzado(laborIdx, centroIdx, 'sacos', e.target.value)}
                                  />
                                </div>
                                <div className="flex-1">
                                  <Label className="text-[10px] text-gray-500">Nota</Label>
                                  <Select
                                    value={centro.nota || 'none'}
                                    onValueChange={(val) => handleUpdateCentroAlzado(laborIdx, centroIdx, 'nota', val === 'none' ? '' : val)}
                                  >
                                    <SelectTrigger className="h-7 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">Sin nota</SelectItem>
                                      <SelectItem value="Mucha basura">Mucha basura</SelectItem>
                                      <SelectItem value="Mucho coyol quemado">Mucho coyol quemado</SelectItem>
                                      <SelectItem value="Mucho coyol pasado">Mucho coyol pasado</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <label className="flex items-center gap-1 text-xs cursor-pointer mt-4">
                                  <input
                                    type="checkbox"
                                    checked={centro.no_terminado || false}
                                    onChange={() => handleToggleCentroNoTerminadoAlzado(laborIdx, centroIdx)}
                                    className="h-3 w-3"
                                  />
                                  <span className="text-gray-500">No terminado</span>
                                </label>
                              </div>
                            </div>
                          ))}
                          {(!labor.centros_alzados || labor.centros_alzados.length === 0) && (
                            <p className="text-xs text-gray-400 italic">No hay centros</p>
                          )}
                          
                          {/* Formulario para agregar nuevo centro de Alzada */}
                          <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded-lg space-y-2">
                            <p className="text-xs font-medium text-orange-800 flex items-center gap-1">
                              <Plus className="w-3 h-3" /> Agregar centro
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <Select value={labor.lote_id} onValueChange={(val) => handleUpdateLoteEdit(laborIdx, val)}>
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue placeholder="Lote" />
                                </SelectTrigger>
                                <SelectContent>
                                  {lotes.map(lote => (
                                    <SelectItem key={lote.id} value={lote.id}>{lote.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select 
                                value={labor.parcela_id} 
                                onValueChange={(val) => handleUpdateParcelaEdit(laborIdx, val)}
                                disabled={!labor.lote_id}
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue placeholder="Parcela" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(parcelasPorLote[labor.lote_id] || []).map(parcela => (
                                    <SelectItem key={parcela.id} value={parcela.id}>Parcela {parcela.numero}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {labor.lote_id && labor.parcela_id && (
                              <>
                                <div className="grid grid-cols-3 gap-2">
                                  <Select 
                                    value={nuevoCentroAlzadoEdit.centro_id} 
                                    onValueChange={(val) => setNuevoCentroAlzadoEdit(prev => ({ ...prev, centro_id: val }))}
                                  >
                                    <SelectTrigger className="h-7 text-xs">
                                      <SelectValue placeholder="Centro" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(() => {
                                        const centros = getCentrosPorParcela(labor.lote_id, parseInt(labor.parcela_id.split('_parcela_')[1]));
                                        const centrosDisponibles = centros.filter(
                                          c => !(labor.centros_alzados || []).some(ca => ca.centro_id === c.id)
                                        );
                                        return centrosDisponibles.map(centro => (
                                          <SelectItem key={centro.id} value={centro.id}>
                                            Centro {centro.numero_centro || centro.numero}
                                          </SelectItem>
                                        ));
                                      })()}
                                    </SelectContent>
                                  </Select>
                                  <Input 
                                    type="number" 
                                    step="0.5"
                                    min="0.5"
                                    className="h-7 text-xs" 
                                    placeholder="Sacos"
                                    value={nuevoCentroAlzadoEdit.sacos}
                                    onChange={(e) => setNuevoCentroAlzadoEdit(prev => ({ ...prev, sacos: e.target.value }))}
                                  />
                                  <Select 
                                    value={nuevoCentroAlzadoEdit.nota || 'none'} 
                                    onValueChange={(val) => setNuevoCentroAlzadoEdit(prev => ({ ...prev, nota: val === 'none' ? '' : val }))}
                                  >
                                    <SelectTrigger className="h-7 text-xs">
                                      <SelectValue placeholder="Nota" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">Sin nota</SelectItem>
                                      <SelectItem value="Mucha basura">Mucha basura</SelectItem>
                                      <SelectItem value="Mucho coyol quemado">Mucho coyol quemado</SelectItem>
                                      <SelectItem value="Mucho coyol pasado">Mucho coyol pasado</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <Button
                                  size="sm"
                                  className="w-full h-7 text-xs bg-orange-600 hover:bg-orange-700"
                                  onClick={() => handleAgregarCentroEditAlzado(laborIdx)}
                                  disabled={!nuevoCentroAlzadoEdit.centro_id || !nuevoCentroAlzadoEdit.sacos}
                                >
                                  <Plus className="w-3 h-3 mr-1" /> Agregar Centro
                                </Button>
                              </>
                            )}
                          </div>
                          
                          {/* Resumen de sacos */}
                          {(labor.centros_alzados || []).length > 0 && (
                            <div className="bg-orange-100 rounded p-2 text-xs text-center">
                              <span className="font-bold text-orange-800">
                                Total: {(labor.centros_alzados || []).reduce((sum, c) => sum + (parseFloat(c.sacos) || 0), 0)} sacos alzados
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Campos para otras labores */}
                      {labor.labor_especifica !== 'corta' && labor.labor_especifica !== 'boyea' && labor.labor_especifica !== 'ajilada' && labor.labor_especifica !== 'coyolea' && labor.labor_especifica !== 'alzada_coyol' && (
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs">Cantidad</Label>
                            <Input
                              type="number"
                              className="h-8"
                              value={labor.cantidad}
                              onChange={(e) => handleUpdateLaborEditable(laborIdx, 'cantidad', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Unidad</Label>
                            <Select
                              value={labor.unidad}
                              onValueChange={(val) => handleUpdateLaborEditable(laborIdx, 'unidad', val)}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unidades">Unidades</SelectItem>
                                <SelectItem value="kilos">Kilos</SelectItem>
                                <SelectItem value="hectareas">Hectáreas</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Horas</Label>
                            <Input
                              type="number"
                              step="0.5"
                              className="h-8"
                              value={labor.horas_trabajadas}
                              onChange={(e) => handleUpdateLaborEditable(laborIdx, 'horas_trabajadas', e.target.value)}
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Observaciones */}
                      <div>
                        <Label className="text-xs">Observaciones</Label>
                        <Input
                          className="h-8"
                          placeholder="Notas..."
                          value={labor.observaciones}
                          onChange={(e) => handleUpdateLaborEditable(laborIdx, 'observaciones', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {laboresEditables.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No hay labores para editar</p>
                </div>
              )}
            </div>
            
            <DialogFooter className="flex gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => { setEditarTrabajador(null); setLaboresEditables([]); }}>
                Cancelar
              </Button>
              <Button 
                className="bg-amber-600 hover:bg-amber-700"
                onClick={handleGuardarEdicion}
                disabled={guardandoEdicion || laboresEditables.length === 0}
              >
                {guardandoEdicion ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Guardar Cambios'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ProduccionLayout>
  );
}
